import React, {useEffect, useMemo, useState, useCallback, useRef} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import {Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, TextField, Dialog, Button, FormControl, Select, Checkbox, FormControlLabel} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ImageIcon from "@mui/icons-material/Image";
import {AudioWaveformIcon, VideoCameraIcon} from "../NewHome/ProjectThumbnail";
import {FilmIcon} from "../LibrettoEditor/FilmIcon";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import TranscribeIcon from "@mui/icons-material/Subtitles";
import {observer} from "mobx-react-lite";
import {ProjectAssetsStore} from "../../state/ProjectAssetsStore";
import {useAssetStore} from "../../storage/AssetStore";
import {
    formatDurationBrief,
    LibrettoProjectTypes,
    IsVideoTrackType,
    GetExtensionFromMime,
    DetectMimeTypeFromBlob
} from "../../utils/utils";
import { maybeLockCanvasToFirstVideo } from "../../utils/canvasSizing";
import {pickFilesAndUpload} from "../../utils/multiFileUpload";
import {validateFilesForUpload} from "../../utils/fileValidation";
import {UnsupportedFilesDialog, UnsupportedFile} from "../../components/UnsupportedFilesDialog";
import {ALL_FORMATS, Input, UrlSource} from "mediabunny";
import {MediaSourceRef} from "../../engine/sources/media-source";
import {AudioClip} from "../../engine/clips";
import {VideoClip} from "../../engine/clips/video/video";
import {CompositionService} from "../../librettoEditor/services/CompositionService";
import {CompositionStateStore} from "../../librettoEditor/store/CompositionStateStore";
import {ApplicationStore} from "../../librettoEditor/store/ApplicationStore";
import {ScriptLayerStore} from "../../librettoEditor/store/ScriptLayerStore";
import {ContextMenuStore} from "../../librettoEditor/store/ContextMenuStore";
import { Timestamp } from "../../engine/models";
import DeleteIcon from "@mui/icons-material/Delete";
import {useConfirmDialog} from "../NewHome/DeleteConfirmation";
import ComputerOutlinedIcon from "@mui/icons-material/ComputerOutlined";
import ViewTimelineOutlinedIcon from "@mui/icons-material/ViewTimelineOutlined";
import {KeyboardArrowDown, KeyboardArrowRight, CreateNewFolderOutlined, FolderOutlined, DriveFileMoveOutlined} from "@mui/icons-material";
import AssetPreviewPopover from "./components/AssetPreviewPopover";
import { CombineFilesDialog } from "./components/CombineFilesDialog";
import { SequenceActionsPopover } from "./components/SequenceActionsPopover";
import {CheckmarkIcon} from "../NewStudio/SettingsIcon";
import {CreateClipForComposition, CreateScriptClipForComposition} from "./utils";
import {SequencePersistenceService} from "../../sequenceEditor/services/SequencePersistenceService";
import type { ScriptLayerInputConfig, SpeakerOverride } from "../../engine/clips/script/script.interfaces";
import {AuthContext} from "../../context/AuthContext";
import {GetTracksLocalAvailability} from "../../storage/LocalAssetStore";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { trackSessionEvent } from "../../analytics/sessionEvents";
import { deriveSequenceTitleFromFileNames, deriveSequenceTitleFromFiles, populateSequenceFromTracks } from "./sequenceAutoCreate";
import DownloadInfoCard, { createIdleDownloadInfoState, type DownloadInfoState } from "./DownloadInfoCard";
import { TRANSCRIPTION_LANGUAGES } from "./transcriptionLanguages";

// Number of speakers options
const SPEAKER_OPTIONS: Array<{ label: string; value: number | 'auto' }> = [
    { label: "Auto", value: 'auto' },
    { label: "1", value: 1 },
    { label: "2", value: 2 },
    { label: "3", value: 3 },
    { label: "4", value: 4 },
    { label: "5", value: 5 },
    { label: "6", value: 6 },
    { label: "7", value: 7 },
    { label: "8", value: 8 },
];

// Re-transcribe Dialog Component
interface ReTranscribeDialogProps {
    isOpen: boolean;
    onClose: () => void;
    trackTitle: string;
    trackId: string;
    onTranscribe: (trackId: string, language: string, numSpeakers: number | 'auto', skipDenoising: boolean) => Promise<void>;
}

const ReTranscribeDialog: React.FC<ReTranscribeDialogProps> = ({
    isOpen,
    onClose,
    trackTitle,
    trackId,
    onTranscribe,
}) => {
    const [language, setLanguage] = useState<string>('auto');
    const [numSpeakers, setNumSpeakers] = useState<number | 'auto'>('auto');
    const [skipDenoising, setSkipDenoising] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleTranscribe = async () => {
        setIsLoading(true);
        try {
            await onTranscribe(trackId, language, numSpeakers, skipDenoising);
            onClose();
        } finally {
            setIsLoading(false);
        }
    };

    // Truncate title if too long
    const displayTitle = trackTitle.length > 45 
        ? trackTitle.substring(0, 45) + '...' 
        : trackTitle;

    const selectStyles = {
        fontFamily: 'Inter',
        fontSize: '13px',
        color: '#1A1A1A',
        backgroundColor: '#FAFAFA',
        borderRadius: '8px',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E5E7EB',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D1D5DB',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#2B6BFD',
            borderWidth: '1px',
        },
        '& .MuiSelect-select': {
            py: 0.875,
            px: 1.5,
        }
    };

    const menuProps = {
        PaperProps: {
            sx: {
                borderRadius: '10px',
                boxShadow: '0px 10px 40px rgba(15, 23, 42, 0.12)',
                maxHeight: 280,
                '& .MuiMenuItem-root': {
                    fontFamily: 'Inter',
                    fontSize: '13px',
                    py: 0.875,
                    '&:hover': {
                        backgroundColor: '#F3F4F5',
                    },
                    '&.Mui-selected': {
                        backgroundColor: '#EBF2FF',
                        '&:hover': {
                            backgroundColor: '#E0EBFF',
                        }
                    }
                }
            }
        }
    };

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '440px',
                    padding: '20px 24px',
                    borderRadius: '12px',
                    boxShadow: '0px 20px 60px rgba(15, 23, 42, 0.08)',
                }
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography sx={{ 
                    fontFamily: 'Inter',
                    fontSize: '15px',
                    fontWeight: 500,
                    lineHeight: '22px',
                    color: '#1A1A1A',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    mr: 1,
                }}>
                    Re-transcribe {displayTitle}
                </Typography>
                <IconButton 
                    size="small" 
                    onClick={onClose}
                    sx={{ 
                        color: '#9CA3AF',
                        padding: '4px',
                        '&:hover': { backgroundColor: 'var(--app-bg-subtle)' }
                    }}
                >
                    <Box component="span" sx={{ fontSize: '18px', lineHeight: 1 }}>×</Box>
                </IconButton>
            </Box>
            
            {/* Subtitle */}
            <Typography sx={{ 
                fontFamily: 'Inter',
                fontSize: '13px',
                fontWeight: 400,
                color: '#6B7280',
                mb: 2.5,
            }}>
                This will replace the existing transcript with a new transcript.
            </Typography>

            {/* Form Fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Language Selection */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ 
                        fontFamily: 'Inter',
                        fontSize: '18px',
                        color: '#808080',
                        width: 24,
                        textAlign: 'center',
                    }}>
                        文
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                            value={language}
                            onChange={(e) => setLanguage(e.target.value)}
                            sx={selectStyles}
                            MenuProps={menuProps}
                        >
                            {Object.entries(TRANSCRIPTION_LANGUAGES).map(([label, code]) => (
                                <MenuItem key={code} value={code}>{label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Number of Speakers Selection */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 8C9.65685 8 11 6.65685 11 5C11 3.34315 9.65685 2 8 2C6.34315 2 5 3.34315 5 5C5 6.65685 6.34315 8 8 8Z" stroke="#808080" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M13.5 14C13.5 11.5 11 10 8 10C5 10 2.5 11.5 2.5 14" stroke="#808080" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </Box>
                    <Typography sx={{
                        fontFamily: 'Inter',
                        fontSize: '13px',
                        color: '#6B7280',
                    }}>
                        Speakers
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Select
                            value={numSpeakers}
                            onChange={(e) => setNumSpeakers(e.target.value as number | 'auto')}
                            sx={selectStyles}
                            MenuProps={menuProps}
                        >
                            {SPEAKER_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Skip Denoising Checkbox - By default, audio is denoised for better transcription */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                    <Box sx={{
                        width: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M8 2V14M4 5V11M12 5V11M2 7V9M14 7V9" stroke="#808080" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </Box>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={skipDenoising}
                                onChange={(e) => setSkipDenoising(e.target.checked)}
                                size="small"
                                sx={{
                                    padding: '4px',
                                    color: '#9CA3AF',
                                    '&.Mui-checked': {
                                        color: '#2B6BFD',
                                    },
                                }}
                            />
                        }
                        label={
                            <Box>
                                <Typography sx={{
                                    fontFamily: 'Inter',
                                    fontSize: '13px',
                                    color: '#1A1A1A',
                                    lineHeight: 1.4,
                                }}>
                                    Skip noise reduction
                                </Typography>
                                <Typography sx={{
                                    fontFamily: 'Inter',
                                    fontSize: '11px',
                                    color: '#9CA3AF',
                                    lineHeight: 1.3,
                                }}>
                                    By default, audio is denoised for better accuracy
                                </Typography>
                            </Box>
                        }
                        sx={{
                            margin: 0,
                            '& .MuiFormControlLabel-label': {
                                marginLeft: '4px',
                            }
                        }}
                    />
                </Box>
            </Box>

            {/* Action Button */}
            <Button
                onClick={handleTranscribe}
                disabled={isLoading}
                fullWidth
                sx={{
                    mt: 2.5,
                    fontFamily: 'Inter',
                    fontSize: '14px',
                    fontWeight: 500,
                    textTransform: 'none',
                    color: '#FFFFFF',
                    backgroundColor: '#1A1A1A',
                    borderRadius: '999px',
                    py: 1.25,
                    '&:hover': {
                        backgroundColor: '#333333',
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#E5E7EB',
                        color: '#9CA3AF',
                    }
                }}
            >
                {isLoading ? 'Transcribing...' : 'Transcribe'}
            </Button>
        </Dialog>
    );
};

// Backup Promotion Dialog Component
interface BackupPromotionDialogProps {
    isOpen: boolean;
    onClose: () => void;
    trackTitle: string;
    backupTrackId: string;
    onPromote: (backupTrackId: string) => Promise<void>;
}

const BackupPromotionDialog: React.FC<BackupPromotionDialogProps> = ({
    isOpen,
    onClose,
    trackTitle,
    backupTrackId,
    onPromote,
}) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handlePromote = async () => {
        setIsLoading(true);
        try {
            await onPromote(backupTrackId);
            onClose();
        } finally {
            setIsLoading(false);
        }
    };

    // Truncate title if too long
    const displayTitle = trackTitle.length > 40 
        ? trackTitle.substring(0, 40) + '...' 
        : trackTitle;

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '420px',
                    padding: '20px 24px',
                    borderRadius: '12px',
                    boxShadow: '0px 20px 60px rgba(15, 23, 42, 0.08)',
                }
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: '#FEF3C7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <InfoOutlinedIcon sx={{ fontSize: 18, color: '#F59E0B' }} />
                    </Box>
                    <Typography sx={{ 
                        fontFamily: 'Inter',
                        fontSize: '15px',
                        fontWeight: 500,
                        lineHeight: '22px',
                        color: '#1A1A1A',
                    }}>
                        Recording incomplete
                    </Typography>
                </Box>
                <IconButton 
                    size="small" 
                    onClick={onClose}
                    sx={{ 
                        color: '#9CA3AF',
                        padding: '4px',
                        mt: -0.5,
                        mr: -1,
                        '&:hover': { backgroundColor: 'var(--app-bg-subtle)' }
                    }}
                >
                    <Box component="span" sx={{ fontSize: '18px', lineHeight: 1 }}>×</Box>
                </IconButton>
            </Box>
            
            {/* Content */}
            <Typography sx={{ 
                fontFamily: 'Inter',
                fontSize: '13px',
                fontWeight: 400,
                color: '#6B7280',
                mb: 1,
                lineHeight: 1.5,
            }}>
                The local recording for <strong style={{ color: '#1A1A1A' }}>{displayTitle}</strong> appears to be incomplete. This can happen if the participant's device disconnected or the browser was closed before the recording finished uploading.
            </Typography>
            
            <Typography sx={{ 
                fontFamily: 'Inter',
                fontSize: '13px',
                fontWeight: 400,
                color: '#6B7280',
                mb: 2,
                lineHeight: 1.5,
            }}>
                A backup recording from the server is available. While slightly lower quality than the local recording, it can be used to recover this track.
            </Typography>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                    onClick={onClose}
                    sx={{
                        flex: 1,
                        fontFamily: 'Inter',
                        fontSize: '13px',
                        fontWeight: 500,
                        textTransform: 'none',
                        color: '#6B7280',
                        backgroundColor: '#F3F4F6',
                        borderRadius: '999px',
                        py: 1,
                        '&:hover': {
                            backgroundColor: '#E5E7EB',
                        },
                    }}
                >
                    Keep waiting
                </Button>
                <Button
                    onClick={handlePromote}
                    disabled={isLoading}
                    sx={{
                        flex: 1,
                        fontFamily: 'Inter',
                        fontSize: '13px',
                        fontWeight: 500,
                        textTransform: 'none',
                        color: '#FFFFFF',
                        backgroundColor: '#F59E0B',
                        borderRadius: '999px',
                        py: 1,
                        '&:hover': {
                            backgroundColor: '#D97706',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#FDE68A',
                            color: '#FFFFFF',
                        }
                    }}
                >
                    {isLoading ? 'Promoting...' : 'Use backup'}
                </Button>
            </Box>
        </Dialog>
    );
};

// Default colors for speaker labels (matching waveform colors)
const DEFAULT_SPEAKER_COLORS = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];

/**
 * Calculate visual dimensions for a video clip being added to a composition.
 * 
 * Strategy:
 * - If video has same aspect ratio as composition canvas: scale to fit (fill canvas)
 * - If different aspect ratio: keep original video dimensions (don't distort)
 * 
 * @param videoWidth - Original video width
 * @param videoHeight - Original video height  
 * @param canvasWidth - Composition canvas width
 * @param canvasHeight - Composition canvas height
 * @returns { width, height, x, y } - Dimensions and center position
 */
function calculateVideoVisualDimensions(
    videoWidth: number,
    videoHeight: number,
    canvasWidth: number,
    canvasHeight: number
): { width: number; height: number; x: number; y: number } {
    // If video dimensions are unknown, default to canvas dimensions
    if (!videoWidth || !videoHeight || videoWidth <= 0 || videoHeight <= 0) {
        return { width: canvasWidth, height: canvasHeight, x: canvasWidth / 2, y: canvasHeight / 2 };
    }
    
    const videoAspect = videoWidth / videoHeight;
    const canvasAspect = canvasWidth / canvasHeight;
    const aspectTolerance = 0.05; // 5% tolerance for "same" aspect ratio
    
    // Check if aspect ratios are approximately equal
    const isSameAspect = Math.abs(videoAspect - canvasAspect) / canvasAspect < aspectTolerance;
    
    let finalWidth: number;
    let finalHeight: number;
    
    if (isSameAspect) {
        // Same aspect ratio: scale to fit canvas (one dimension matches exactly)
        if (videoAspect > canvasAspect) {
            // Video is wider - fit to canvas width
            finalWidth = canvasWidth;
            finalHeight = canvasWidth / videoAspect;
        } else {
            // Video is taller - fit to canvas height
            finalHeight = canvasHeight;
            finalWidth = canvasHeight * videoAspect;
        }
    } else {
        // Different aspect ratio: keep original dimensions
        finalWidth = videoWidth;
        finalHeight = videoHeight;
    }
    
    // Center on canvas
    return {
        width: finalWidth,
        height: finalHeight,
        x: canvasWidth / 2,
        y: canvasHeight / 2
    };
}

interface MediaItem {
    id: string;
    title: string;
    name?: string;             // display name (files mapping uses 'name')
    thumbnailUrl?: string;
    duration?: number;
    type: 'image' | 'video' | 'audio';
    status: string;
    progress?: number;
    track?: any;
    record?: any;              // underlying asset/track record
}

const PaperPanel: React.FC<{
    title: string;
    onAdd?: (anchorEl?: HTMLElement) => void;
    children: React.ReactNode;
    fill?: boolean; // when true, fills remaining column height
    highlight?: boolean;
    dataTour?: string;
    addButtonDataTour?: string;
}> = ({ title, onAdd, children, fill, highlight, dataTour, addButtonDataTour }) => {
    return (
        <Box
            data-tour={dataTour}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                background: 'var(--app-bg-surface)',
                border: `0.5px solid ${highlight ? 'rgba(59, 130, 246, 0.6)' : 'var(--app-border-subtle)'}`,
                borderRadius: '12px',
                px: 1,
                py: 1,
                boxShadow: highlight ? '0 0 0 2px rgba(59, 130, 246, 0.14)' : undefined,
                transition: 'box-shadow 160ms ease, border-color 160ms ease',
                ...(fill ? { flex: 1, minHeight: 0 } : {}),
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 0.5,
                    py: 0.5,
                }}
            >
                <Typography sx={{ fontSize: 14, fontWeight: 600, userSelect: 'none', color: 'var(--app-text-primary)' }}>{title}</Typography>
                <IconButton
                    data-tour={addButtonDataTour}
                    size="small"
                    onClick={(e) => onAdd?.(e.currentTarget)}
                    sx={{ color: 'var(--app-text-muted)' }}
                >
                    <AddIcon fontSize="small" />
                </IconButton>
            </Box>

            <Divider sx={{ my: 0.5 }} />

            <Box sx={{ ...(fill ? { overflow: 'auto' } : {}) }}>
                {children}
            </Box>
        </Box>
    );
};

// Reusable row (shows 3-dots on hover like MediaPanelContainer)
const RowItem: React.FC<{
    icon: React.ReactNode;
    primary: string;
    disabled?: boolean;
    onClick?: (e?: React.MouseEvent) => void;
    onMenu?: (e: React.MouseEvent) => void;
    indent?: number;                 // extra left padding for hierarchical items
    iconBgColor?: string;            // background for the icon container (default grey)
    dataTour?: string;
}> = ({ icon, primary, disabled, onClick, onMenu, indent, iconBgColor, dataTour }) => {
    const handleContextMenu = (e: React.MouseEvent) => {
        if (!onMenu) return;
        e.preventDefault();
        e.stopPropagation();
        onMenu(e);
    };

    return (
        <ListItem
            disablePadding
            onContextMenu={handleContextMenu}
            sx={{
                '&:hover': {
                    backgroundColor: 'var(--app-bg-subtle)',
                    borderRadius: '6px',
                },
                // reveal action button on hover
                '&:hover .row-actions': {
                    opacity: 1,
                    pointerEvents: 'auto',
                },
            }}
            secondaryAction={
                <IconButton
                    size="small"
                    className="row-actions"
                    onClick={onMenu}
                    sx={{ opacity: 0, transition: 'opacity 0.2s ease' }}
                >
                    <MoreVertIcon fontSize="small" />
                </IconButton>
            }
        >
            <ListItemButton data-tour={dataTour} onClick={(e) => onClick?.(e)} disabled={disabled} sx={{ py: 0.75, pr: 4, pl: indent ? indent : undefined }}>
                <ListItemIcon sx={{ minWidth: 32, mr: 1 }}>
                    <Box
                        sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '4px',
                            background: iconBgColor ?? 'var(--app-bg-subtle)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                        }}
                    >
                        {icon}
                    </Box>
                </ListItemIcon>
                <ListItemText
                    primaryTypographyProps={{
                        fontSize: 13,
                        fontWeight: 400,
                        noWrap: true,
                        color: 'var(--app-text-primary)',
                    }}
                    primary={primary}
                />
            </ListItemButton>
        </ListItem>
    );
};

// @ts-ignore
export const V2ProjectSidebar: React.FC<any> = observer(({handleCreateNewSequence, handleOpenSequence, selectedCompositionId, setSelectedCompositionTitle, setSelectedCompositionId, highlightFilesPanel}) => {
    const { GetThumbnailUrl, GetWaveformData, GetTranscript } = useAssetStore();
    const { DeleteAsset, DeleteTrack, RenameTrackOrAsset, DeleteSequence, RenameSequence } = useAssetStore();
    const { GetTrackUrl, GetAssetUrl, CreateComposition, ResolveAssetUrl, CreateSpeaker } = useAssetStore();
    const { RenameComposition, DuplicateComposition, DeleteComposition } = useAssetStore();
    const { UploadAsset, RetranscribeTrack, GetEnhancedAudioUrl, PromoteBackupTrack, CreateSequence } = useAssetStore();
    const { CreateFolder, RenameFolder, DeleteFolder, MoveFileToFolder } = useAssetStore();
    const authContext = React.useContext(AuthContext);
    const [thumbs, setThumbs] = useState<Record<string, string | undefined>>({});
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [menuItem, setMenuItem] = useState<any | null>(null);
    const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null);
    // Preview popover state
    const [previewAnchorEl, setPreviewAnchorEl] = useState<HTMLElement | null>(null);
    const [previewItem, setPreviewItem] = useState<any | null>(null);
    const [previewMode, setPreviewMode] = useState<"single" | "multi">("single");
    const [selectedTrackIds, setSelectedTrackIds] = useState<Set<string>>(new Set());
    // Sequence actions popover state
    const [sequencePopoverAnchorEl, setSequencePopoverAnchorEl] = useState<HTMLElement | null>(null);
    const [sequencePopoverItem, setSequencePopoverItem] = useState<any | null>(null);
    // Unsupported files dialog state
    const [unsupportedFilesDialogOpen, setUnsupportedFilesDialogOpen] = useState<boolean>(false);
    const [unsupportedFiles, setUnsupportedFiles] = useState<UnsupportedFile[]>([]);
    const [openReviewAfterCombine, setOpenReviewAfterCombine] = useState<boolean>(false);
    // Multi-file combine dialog state (Files panel uploads)
    const [combineDialogOpen, setCombineDialogOpen] = useState<boolean>(false);
    const [pendingCombineFiles, setPendingCombineFiles] = useState<Array<{ file: File }>>([]);
    const [multiUploadAssets, setMultiUploadAssets] = useState<Array<{ assetId: string; fileName: string; uploadIndex: number }>>([]);
    const [multiUploadMode, setMultiUploadMode] = useState<'separate' | 'sequence' | null>(null);
    const [sequenceIdForMultiUpload, setSequenceIdForMultiUpload] = useState<string | null>(null);
    const [sequenceTitleForMultiUpload, setSequenceTitleForMultiUpload] = useState<string | null>(null);
    const multiUploadProcessingRef = useRef(false);
    // Compositions from store
    const compositions = ProjectAssetsStore.projectCompositions;
    const [renamingItemId, setRenamingItemId] = useState<string | null>(null);
    const [renameValue, setRenameValue] = useState("");

    const images = ProjectAssetsStore.projectImages;
    const tracks = ProjectAssetsStore.projectTracks;
    const sequences = ProjectAssetsStore.projectSequences;
    const folders = ProjectAssetsStore.projectFolders;

    const {openDialog, ConfirmDialogComponent} = useConfirmDialog({confirmText: 'Delete', cancelText: 'Cancel'});

    const [sequencesExpanded, setSequencesExpanded] = useState<boolean>(false);
    // Track which folders are expanded (by folderId)
    const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
    // Drag state for file drag-and-drop into folders
    const [draggedFileId, setDraggedFileId] = useState<string | null>(null);
    const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
    // Track local availability for tracks (trackId -> isLocallyAvailable)
    const [localAvailability, setLocalAvailability] = useState<Map<string, boolean>>(new Map());
    // Re-transcribe dialog state
    const [reTranscribeDialogOpen, setReTranscribeDialogOpen] = useState<boolean>(false);
    const [reTranscribeTrackInfo, setReTranscribeTrackInfo] = useState<{ id: string; title: string } | null>(null);
    // Backup promotion dialog state
    const [backupDialogOpen, setBackupDialogOpen] = useState<boolean>(false);
    const [backupTrackInfo, setBackupTrackInfo] = useState<{ title: string; backupTrackId: string } | null>(null);
    // Download feedback state (small progress card shown in sidebar)
    const [downloadFeedback, setDownloadFeedback] = useState<DownloadInfoState>(() => createIdleDownloadInfoState());
    const downloadAbortRef = useRef<AbortController | null>(null);
    const downloadDismissTimerRef = useRef<number | null>(null);

    const clearDownloadDismissTimer = useCallback(() => {
        if (downloadDismissTimerRef.current !== null) {
            window.clearTimeout(downloadDismissTimerRef.current);
            downloadDismissTimerRef.current = null;
        }
    }, []);

    const scheduleDownloadDismiss = useCallback((delayMs: number) => {
        clearDownloadDismissTimer();
        downloadDismissTimerRef.current = window.setTimeout(() => {
            setDownloadFeedback(createIdleDownloadInfoState());
            downloadDismissTimerRef.current = null;
        }, delayMs);
    }, [clearDownloadDismissTimer]);

    const showDownloadError = useCallback((fileName: string, message: string) => {
        setDownloadFeedback({
            status: 'error',
            fileName,
            progressPercent: null,
            downloadedBytes: 0,
            totalBytes: null,
            errorMessage: message,
        });
        scheduleDownloadDismiss(4500);
    }, [scheduleDownloadDismiss]);

    const dismissDownloadCard = useCallback(() => {
        clearDownloadDismissTimer();
        setDownloadFeedback(createIdleDownloadInfoState());
    }, [clearDownloadDismissTimer]);

    const cancelDownload = useCallback(() => {
        clearDownloadDismissTimer();
        if (downloadAbortRef.current) {
            downloadAbortRef.current.abort();
            return;
        }
        setDownloadFeedback(createIdleDownloadInfoState());
    }, [clearDownloadDismissTimer]);

    // If we need to show the review modal but a combine modal is already open, defer until combine closes.
    useEffect(() => {
        if (combineDialogOpen) return;
        if (!openReviewAfterCombine) return;
        setOpenReviewAfterCombine(false);
        if (unsupportedFiles.length === 0) return;
        setUnsupportedFilesDialogOpen(true);
    }, [combineDialogOpen, openReviewAfterCombine, unsupportedFiles.length]);

    useEffect(() => {
        return () => {
            clearDownloadDismissTimer();
            if (downloadAbortRef.current) {
                downloadAbortRef.current.abort();
                downloadAbortRef.current = null;
            }
        };
    }, [clearDownloadDismissTimer]);

    const files = [
        ...images.map((img: any) => ({
            id: String(img.id),
            name: img.title || img.name || img.fileName || img.originalName || String(img.id),
            type: 'image' as const,
            thumbKey: img.thumbnailId as string | undefined,
            record: img,
        })),
        ...tracks.map((tr: any) => {
            const isVideoTrack = IsVideoTrackType(tr.trackType);
            const type = isVideoTrack ? 'video' : 'audio';
            return {
                id: String(tr.id),
                name: tr.title || tr.filename || tr.fileName || tr.originalName || String(tr.id),
                type,
                // for tracks, prefer hasThumbnail flag; thumbnails are fetched via trackId
                thumbKey: undefined,
                record: tr,
            } as const;
        }),
    ];

    const isImageFile = (file: File): boolean =>
        Boolean(file?.type?.startsWith("image/")) || /\.(bmp|jpe?g|png|gif|webp)$/i.test(String(file?.name ?? ""));

    const getUploadFiletype = (file: File): string => {
        if (file?.type?.startsWith("image/")) return "ImageUpload";
        if (file?.type?.startsWith("video/")) return "video";
        if (file?.type?.startsWith("audio/")) return "audio";
        // Fallback: keep the prior behavior (treat unknown as audio); validation should prevent unsupported.
        return "audio";
    };

    const handleKeepSeparateMultiUpload = useCallback(() => {
        setCombineDialogOpen(false);
        setPendingCombineFiles([]);
        setMultiUploadMode("separate");
        setSequenceIdForMultiUpload(null);
        setSequenceTitleForMultiUpload(null);
    }, []);

    const handleCombineIntoSequenceMultiUpload = useCallback(async () => {
        const files = pendingCombineFiles.map((p) => p.file);
        setCombineDialogOpen(false);
        setPendingCombineFiles([]);

        const projectId = ProjectAssetsStore.projectId as string;
        if (!projectId) return;

        const sequenceTitle = deriveSequenceTitleFromFiles(files);
        setSequenceTitleForMultiUpload(sequenceTitle);

        try {
            const sequenceId = await CreateSequence({ projectId, title: sequenceTitle });
            if (!sequenceId) return;
            setSequenceIdForMultiUpload(sequenceId);
            setMultiUploadMode("sequence");
        } catch (e) {
            console.error("[V2ProjectSidebar] Failed to create sequence for multi-upload", e);
            setMultiUploadMode(null);
            setSequenceIdForMultiUpload(null);
            setSequenceTitleForMultiUpload(null);
        }
    }, [CreateSequence, pendingCombineFiles]);

    // When user chose to combine multi-uploaded files, persist a real Sequence (layers+clips) once tracks are present.
    useEffect(() => {
        if (!multiUploadMode || multiUploadAssets.length === 0) return;

        const projectId = ProjectAssetsStore.projectId as string;
        if (!projectId) return;

        const stripExt = (name: string) => String(name ?? "").replace(/\.[^/.]+$/, "");
        const hasDigits = (name: string) => /\d/.test(stripExt(name));
        const shouldOrderByFilename = multiUploadAssets.length > 1 && multiUploadAssets.every((a) => hasDigits(a.fileName));
        const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
        const orderedAssets = [...multiUploadAssets].sort((a, b) => {
            if (!shouldOrderByFilename) return a.uploadIndex - b.uploadIndex;
            return collator.compare(a.fileName, b.fileName);
        });
        const orderedAssetIds = orderedAssets.map((a) => a.assetId);

        const tracks = ProjectAssetsStore.projectTracks || [];
        const availableTracks = orderedAssetIds
            .map((assetId) => tracks.find((t: any) => t.assetId === assetId))
            .filter((t): t is any => t !== undefined);

        if (availableTracks.length < orderedAssetIds.length) return;

        if (multiUploadMode === "separate") {
            setMultiUploadAssets([]);
            setMultiUploadMode(null);
            setSequenceIdForMultiUpload(null);
            setSequenceTitleForMultiUpload(null);
            return;
        }

        if (multiUploadMode !== "sequence" || !sequenceIdForMultiUpload) return;
        if (multiUploadProcessingRef.current) return;

        const userId = authContext.getUserId?.();
        const workspaceId =
            authContext.getCurrentWorkspaceId?.() ||
            authContext.getDefaultWorkspaceId?.() ||
            userId ||
            null;
        if (!userId) return;

        multiUploadProcessingRef.current = true;
        void (async () => {
            try {
                const layerNames = orderedAssets.map((a) => stripExt(a.fileName));
                await populateSequenceFromTracks({
                    sequenceId: sequenceIdForMultiUpload,
                    projectId,
                    userId,
                    workspaceId,
                    tracks: availableTracks,
                    layerNames,
                });
            } catch (e) {
                console.error("[V2ProjectSidebar] Failed to populate sequence for multi-upload", e);
            } finally {
                multiUploadProcessingRef.current = false;
                setMultiUploadAssets([]);
                setMultiUploadMode(null);
                setSequenceIdForMultiUpload(null);
                setSequenceTitleForMultiUpload(null);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [multiUploadMode, multiUploadAssets, sequenceIdForMultiUpload, ProjectAssetsStore.projectTracks]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const updates: Record<string, string | undefined> = {};
            for (const f of files) {
                if (thumbs[f.id] != null) continue;
                try {
                    if (f.type === 'audio') {
                        // never fetch thumbnails for audio
                        continue;
                    }
                    if (f.type === 'video') {
                        if (!f.record?.hasThumbnail) continue;
                        const url = await GetThumbnailUrl({ thumbnailId: f.id, projectId: ProjectAssetsStore.projectId as string });
                        if (!cancelled) updates[f.id] = url;
                        continue;
                    }
                    if (f.type === 'image') {
                        if (!f.thumbKey) continue; // requires thumbnailId
                        const url = await GetThumbnailUrl({ thumbnailId: f.thumbKey, projectId: ProjectAssetsStore.projectId as string });
                        if (!cancelled) updates[f.id] = url;
                        continue;
                    }
                } catch {}
            }
            if (!cancelled && Object.keys(updates).length) {
                setThumbs(prev => ({ ...prev, ...updates }));
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);

    // Check local availability for all tracks (for local-first uploads)
    useEffect(() => {
        let cancelled = false;
        const trackIds = tracks.map((tr: any) => tr.id || tr.trackId).filter(Boolean);
        if (trackIds.length === 0) {
            setLocalAvailability(new Map());
            return;
        }
        
        (async () => {
            const availability = await GetTracksLocalAvailability(trackIds);
            if (!cancelled) {
                setLocalAvailability(availability);
            }
        })();
        
        return () => { cancelled = true; };
    }, [tracks]);

    /**
     * Determine if a file (track) is ready to use.
     * A track is ready if:
     * - It exists locally in Dexie (local-first uploads), OR
     * - Its status on Firestore is "Ready" (case-insensitive)
     */
    const isFileReady = (file: { id: string; type: string; record?: any }): boolean => {
        // Images are always ready (no processing involved)
        if (file.type === 'image') return true;
        
        const trackId = file.id;
        const trackRecord = file.record;
        
        // Check server status first (case-insensitive) - most common case
        const status = trackRecord?.status;
        if (status && status.toLowerCase() === 'ready') return true;
        
        // Check if locally available (local-first uploads)
        if (localAvailability.get(trackId)) return true;
        
        return false;
    };

    const isFileRemoteSynced = (file: { type: string; record?: any }): boolean => {
        const status = String(file.record?.status || '').trim().toLowerCase();
        return status === 'ready' || status === 'processing';
    };

    /**
     * Get a human-readable status label for a non-ready track.
     * Returns null if track is ready (no status to show).
     */
    const getFileStatusLabel = (file: { id: string; type: string; record?: any }): string | null => {
        if (file.type === 'image') return null;
        if (isFileReady(file)) return null;
        
        const status = file.record?.status;
        if (!status) return 'Preparing';
        
        // Normalize status strings (case-insensitive checks)
        const statusLower = status.toLowerCase();
        if (statusLower === 'processing...' || statusLower === 'processing') return 'Preparing';
        if (statusLower === 'uploading...' || statusLower === 'uploading') return 'Preparing';
        if (statusLower === 'recording in progress' || statusLower === 'recording') return 'Recording';
        
        // Don't show "Ready" as a label - that's handled by isFileReady
        if (statusLower === 'ready') return null;
        
        return status;
    };

    /**
     * Get transcription status info for a ready track.
     * Only returns info when track is ready AND transcription is in progress, failed, or silent.
     * Returns null for completed, cancelled, not_needed, or when track isn't ready yet.
     */
    const getTranscriptionStatus = (file: { id: string; type: string; record?: any }): { label: string; isError: boolean; isSilent?: boolean } | null => {
        // Only show transcription status for ready tracks (audio/video only)
        if (file.type === 'image') return null;
        if (!isFileReady(file)) return null;
        
        const transcriptionState = file.record?.transcriptionState;
        if (!transcriptionState) return null;
        
        const stateLower = transcriptionState.toLowerCase();
        
        // Show "Transcribing" for queued, processing, client_extracting, or pending
        if (stateLower === 'queued' || 
            stateLower === 'processing' || 
            stateLower === 'client_extracting' || 
            stateLower === 'pending') {
            return { label: 'Transcribing', isError: false };
        }
        
        // Show "Transcription failed" for failed
        if (stateLower === 'failed') {
            return { label: 'Transcription failed', isError: true };
        }
        
        // Show "Silent track" for silent - no speech detected
        if (stateLower === 'silent') {
            return { label: 'Silent - no speech detected', isError: false, isSilent: true };
        }
        
        // Don't show anything for completed, cancelled, or not_needed
        return null;
    };

    /**
     * Check if a track has a backup available that can be promoted.
     * This is shown when a track is stuck in "recording" state and has a backup available.
     */
    const getBackupAvailableInfo = (file: { id: string; type: string; record?: any }): { backupTrackId: string } | null => {
        if (file.type === 'image') return null;
        
        const trackRecord = file.record;
        if (!trackRecord) return null;
        
        const status = String(trackRecord.status || '').toLowerCase();
        const backupTrackId = trackRecord.backupTrackId;
        const backupAvailableAt = trackRecord.backupAvailableAt;
        
        // Check if backup option should be shown:
        // - Track is stuck in "recording" state
        // - Has a linked backup track ID
        // - Backup availability time has passed
        if (
            status === 'recording' &&
            backupTrackId &&
            backupTrackId !== '' &&
            backupAvailableAt !== undefined &&
            Date.now() >= backupAvailableAt
        ) {
            return { backupTrackId };
        }
        
        return null;
    };

    const fileIconFor = (t: string) => {
        switch (t) {
            case 'image': return <ImageIcon sx={{ fontSize: 18, color: 'var(--app-text-muted)' }} />;
            case 'audio': return <AudioWaveformIcon sx={{ fontSize: 18, color: 'var(--app-text-muted)' }} />;
            case 'video': return <VideoCameraIcon backgroundColor="var(--app-bg-subtle)" />;
            default: return <AudioWaveformIcon sx={{ fontSize: 18, color: 'var(--app-text-muted)' }} />;
        }
    };

    // Toggle folder expand/collapse
    const toggleFolder = (folderId: string) => {
        setExpandedFolders(prev => {
            const next = new Set(prev);
            if (next.has(folderId)) {
                next.delete(folderId);
            } else {
                next.add(folderId);
            }
            return next;
        });
    };

    // Get files that belong to a specific folder
    const getFilesInFolder = (folderId: string) => {
        const folder = folders.find((f: any) => f.id === folderId);
        const fileIds = folder?.fileIds || [];
        return files.filter(f => fileIds.includes(f.id));
    };

    // Get files that don't belong to any folder (unfiled)
    const getUnfiledFiles = useMemo(() => {
        const allFolderFileIds = new Set<string>();
        for (const folder of folders) {
            for (const fileId of (folder.fileIds || [])) {
                allFolderFileIds.add(fileId);
            }
        }
        return files.filter(f => !allFolderFileIds.has(f.id));
    }, [files, folders]);

    const selectedPreviewItems = useMemo(() => {
        if (selectedTrackIds.size === 0) return [];
        return files
            .filter(
                (file) =>
                    selectedTrackIds.has(String(file.id)) &&
                    (file.type === "audio" || file.type === "video"),
            )
            .map((file) => ({
                id: String(file.id),
                name: String(file.name ?? ""),
                type: file.type as "audio" | "video",
                thumbnailUrl: file.type === "video" ? thumbs[file.id] : undefined,
            }));
    }, [files, selectedTrackIds, thumbs]);

    // Find which folder a file belongs to (if any)
    const findFolderForFile = (fileId: string): string | undefined => {
        for (const folder of folders) {
            if ((folder.fileIds || []).includes(fileId)) {
                return folder.id;
            }
        }
        return undefined;
    };

    const createSequenceFromFolder = async (folder: any) => {
        const folderId = folder?.id;
        if (!folderId) return;

        const folderFiles = getFilesInFolder(folderId);
        const mediaFiles = folderFiles.filter((file) => file.type === "audio" || file.type === "video");
        if (mediaFiles.length === 0) {
            console.warn("[V2ProjectSidebar] Folder has no audio/video files for sequence creation");
            return;
        }

        const tracksForSequence = mediaFiles
            .map((file) => file.record)
            .filter((track): track is any => Boolean(track));
        if (tracksForSequence.length === 0) {
            console.warn("[V2ProjectSidebar] Folder media items have no track records for sequence creation");
            return;
        }

        const projectId = ProjectAssetsStore.projectId as string;
        if (!projectId) return;

        const userId = authContext.getUserId?.();
        const workspaceId =
            authContext.getCurrentWorkspaceId?.() ||
            authContext.getDefaultWorkspaceId?.() ||
            userId ||
            null;
        if (!userId) {
            console.warn("[V2ProjectSidebar] Missing user identity; cannot create sequence from folder");
            return;
        }

        const baseNames = mediaFiles.map((file) => String(file.name ?? ""));
        const sequenceTitle = deriveSequenceTitleFromFileNames(baseNames) || String(folder?.name || "Sequence");
        const sequenceId = await CreateSequence({ projectId, title: sequenceTitle });
        if (!sequenceId) {
            console.warn("[V2ProjectSidebar] Failed to create sequence for folder", { folderId });
            return;
        }

        const layerNames = mediaFiles.map((file) => String(file.name ?? "").replace(/\.[^/.]+$/, ""));
        await populateSequenceFromTracks({
            sequenceId,
            projectId,
            userId,
            workspaceId,
            tracks: tracksForSequence,
            layerNames,
        });
        setSequencesExpanded(true);
    };

    const createSequenceFromSelectedTracks = async () => {
        const selectedIds = Array.from(selectedTrackIds);
        if (selectedIds.length < 2) return;

        const selectedMediaFiles = selectedIds
            .map((id) => files.find((file) => String(file.id) === String(id)))
            .filter(
                (file): file is (typeof files)[number] =>
                    Boolean(file) && (file.type === "audio" || file.type === "video"),
            );

        if (selectedMediaFiles.length < 2) {
            console.warn("[V2ProjectSidebar] Need at least two selected audio/video tracks to create a sequence");
            return;
        }

        const tracksForSequence = selectedMediaFiles
            .map((file) => file.record)
            .filter((track): track is any => Boolean(track));
        if (tracksForSequence.length < 2) {
            console.warn("[V2ProjectSidebar] Selected media items are missing track records for sequence creation");
            return;
        }

        const projectId = ProjectAssetsStore.projectId as string;
        if (!projectId) return;

        const userId = authContext.getUserId?.();
        const workspaceId =
            authContext.getCurrentWorkspaceId?.() ||
            authContext.getDefaultWorkspaceId?.() ||
            userId ||
            null;
        if (!userId) {
            console.warn("[V2ProjectSidebar] Missing user identity; cannot create sequence from selection");
            return;
        }

        const baseNames = selectedMediaFiles.map((file) => String(file.name ?? ""));
        const sequenceTitle = deriveSequenceTitleFromFileNames(baseNames) || "Sequence";
        const sequenceId = await CreateSequence({ projectId, title: sequenceTitle });
        if (!sequenceId) {
            console.warn("[V2ProjectSidebar] Failed to create sequence from selected tracks");
            return;
        }

        const layerNames = selectedMediaFiles.map((file) => String(file.name ?? "").replace(/\.[^/.]+$/, ""));
        await populateSequenceFromTracks({
            sequenceId,
            projectId,
            userId,
            workspaceId,
            tracks: tracksForSequence,
            layerNames,
        });

        setSequencesExpanded(true);
        setSelectedTrackIds(new Set());
    };

    // Handle folder context menu
    const handleFolderMenu = (event: React.MouseEvent, folder: any) => {
        event.preventDefault();
        event.stopPropagation();
        const folderId = folder.id;
        const currentName = folder.name || "Untitled Folder";
        const filesInFolder = getFilesInFolder(folderId);
        const fileCount = filesInFolder.length;
        const menuItems = [
            {
                title: "Create sequence from files",
                icon: () => <ViewTimelineOutlinedIcon />,
                onClick: () => {
                    void createSequenceFromFolder(folder).catch((error) => {
                        console.error("[V2ProjectSidebar] Failed to create sequence from folder", error);
                    });
                    ContextMenuStore.hide();
                },
            },
            {
                title: "Rename",
                icon: () => <ModeEditIcon />,
                onClick: () => {
                    setRenamingItemId(folderId);
                    setRenameValue(currentName);
                    ContextMenuStore.hide();
                },
            },
            {
                title: "Delete",
                icon: () => <DeleteIcon />,
                onClick: () => {
                    openDialog({
                        title: `Delete this folder?`,
                        message: fileCount > 0 
                            ? `This will permanently delete the folder and ${fileCount} file${fileCount > 1 ? 's' : ''} inside it.`
                            : 'This folder will be permanently deleted.',
                        onConfirm: async () => {
                            try {
                                // Delete all files inside the folder first
                                for (const file of filesInFolder) {
                                    if (file.type === 'image') {
                                        await DeleteAsset({ assetId: file.id });
                                    } else {
                                        await DeleteTrack({ trackId: file.id });
                                    }
                                }
                                // Then delete the folder itself
                                await DeleteFolder({ folderId });
                            } catch (_) {}
                        },
                    });
                    ContextMenuStore.hide();
                },
            },
        ];
        ContextMenuStore.show(event.clientX, event.clientY, menuItems);
    };

    // Drag-and-drop handlers for files
    const handleFileDragStart = (e: React.DragEvent, fileId: string) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', fileId);
        setDraggedFileId(fileId);
    };

    const handleFileDragEnd = () => {
        setDraggedFileId(null);
        setDragOverFolderId(null);
    };

    const handleFolderDragOver = (e: React.DragEvent, folderId: string) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverFolderId(folderId);
    };

    const handleFolderDragLeave = () => {
        setDragOverFolderId(null);
    };

    const handleFolderDrop = async (e: React.DragEvent, toFolderId: string | null) => {
        e.preventDefault();
        const fileId = e.dataTransfer.getData('text/plain');
        if (!fileId) return;

        const fromFolderId = findFolderForFile(fileId);
        // Don't move if already in the target folder
        if (fromFolderId === toFolderId) {
            setDraggedFileId(null);
            setDragOverFolderId(null);
            return;
        }

        await MoveFileToFolder({ fileId, fromFolderId, toFolderId: toFolderId || undefined });
        setDraggedFileId(null);
        setDragOverFolderId(null);
    };

    const titleTextStyle = {
        color: 'var(--app-text-primary)',
        paddingLeft: '8px',            // px-2
        paddingRight: '8px',
        flex: 1,                       // flex-1
        overflow: 'hidden',            // overflow-hidden
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: '14px',              // body-2-medium (typically ~14px)
        fontWeight: 500,               // medium
        lineHeight: '20px',            // matches Tailwind body line-height
        fontFamily: 'Inter, sans-serif',
        userSelect: 'none',
    };

    const startRenaming = (item: MediaItem) => {
        setRenamingItemId(item.id);
        setRenameValue(item.title);
    };

    const normalizeMime = (mime: string | null | undefined): string =>
        String(mime || '').split(';')[0].trim().toLowerCase();

    const isGenericMime = (mime: string): boolean =>
        !mime || mime === 'application/octet-stream' || mime === 'binary/octet-stream';

    const extensionFromMime = (mime: string): string => {
        if (!mime) return '';
        if (mime === 'audio/mp4') return '.m4a';
        if (mime === 'video/quicktime') return '.mov';
        if (mime === 'image/webp') return '.webp';
        if (mime === 'audio/flac' || mime === 'audio/x-flac') return '.flac';
        return GetExtensionFromMime(mime);
    };

    const downloadFile = useCallback(async (url: string, name: string, mimeHint?: string) => {
        clearDownloadDismissTimer();
        if (downloadAbortRef.current) {
            downloadAbortRef.current.abort();
            downloadAbortRef.current = null;
        }

        const baseName = (name || 'download').trim() || 'download';
        const abortController = new AbortController();
        downloadAbortRef.current = abortController;

        setDownloadFeedback({
            status: 'starting',
            fileName: baseName,
            progressPercent: null,
            downloadedBytes: 0,
            totalBytes: null,
            errorMessage: null,
        });

        try {
            const resp = await fetch(url, { signal: abortController.signal });
            if (!resp.ok) {
                throw new Error(`Request failed (${resp.status})`);
            }

            const contentType = resp.headers.get('content-type') || '';
            const contentLengthRaw = Number(resp.headers.get('content-length') || '');
            const totalBytes = Number.isFinite(contentLengthRaw) && contentLengthRaw > 0 ? contentLengthRaw : null;

            let downloadedBytes = 0;
            let blob: Blob;
            if (resp.body) {
                const reader = resp.body.getReader();
                const chunks: Uint8Array[] = [];
                let lastProgressUpdateMs = 0;

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    if (!value) continue;

                    chunks.push(value);
                    downloadedBytes += value.byteLength;

                    const now = Date.now();
                    if (now - lastProgressUpdateMs >= 120) {
                        setDownloadFeedback({
                            status: 'downloading',
                            fileName: baseName,
                            progressPercent: totalBytes
                                ? Math.min(99, Math.round((downloadedBytes / totalBytes) * 100))
                                : null,
                            downloadedBytes,
                            totalBytes,
                            errorMessage: null,
                        });
                        lastProgressUpdateMs = now;
                    }
                }

                blob = new Blob(chunks, { type: contentType || 'application/octet-stream' });
            } else {
                blob = await resp.blob();
                downloadedBytes = blob.size;
            }

            let finalName = baseName;
            if (!/\.[a-z0-9]{2,5}$/i.test(finalName)) {
                let mime = normalizeMime(contentType);
                if (isGenericMime(mime)) {
                    mime = normalizeMime(blob.type);
                }
                if (isGenericMime(mime)) {
                    mime = normalizeMime(mimeHint);
                }
                if (isGenericMime(mime)) {
                    const detectedMime = await DetectMimeTypeFromBlob(blob);
                    mime = normalizeMime(detectedMime);
                }
                const extFromMime = extensionFromMime(mime);
                finalName = finalName + (extFromMime || '');
            }

            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = objectUrl;
            a.download = finalName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);

            setDownloadFeedback({
                status: 'completed',
                fileName: finalName,
                progressPercent: 100,
                downloadedBytes: blob.size || downloadedBytes,
                totalBytes: totalBytes ?? blob.size ?? null,
                errorMessage: null,
            });
            scheduleDownloadDismiss(3500);
        } catch (e) {
            if (abortController.signal.aborted) {
                setDownloadFeedback({
                    status: 'cancelled',
                    fileName: baseName,
                    progressPercent: null,
                    downloadedBytes: 0,
                    totalBytes: null,
                    errorMessage: null,
                });
                scheduleDownloadDismiss(2000);
                return;
            }

            console.warn('Download failed', e);
            const message = e instanceof Error ? e.message : 'Unable to download file.';
            setDownloadFeedback({
                status: 'error',
                fileName: baseName,
                progressPercent: null,
                downloadedBytes: 0,
                totalBytes: null,
                errorMessage: message,
            });
            scheduleDownloadDismiss(4500);
        } finally {
            if (downloadAbortRef.current === abortController) {
                downloadAbortRef.current = null;
            }
        }
    }, [clearDownloadDismissTimer, scheduleDownloadDismiss]);

    const handleContextMenuClick = (event: React.MouseEvent, item: MediaItem, isReady: boolean = true) => {
        event.preventDefault();
        event.stopPropagation();

        // For non-ready tracks, only show Rename and Delete options
        if (!isReady) {
            const limitedMenuItems = [
                {
                    title: "Rename",
                    icon: () => <ModeEditIcon />,
                    onClick: () => {
                        startRenaming(item);
                        ContextMenuStore.hide();
                    },
                },
                {
                    title: "Delete",
                    icon: () => <DeleteIcon />,
                    onClick: () => {
                        openDialog({
                            title: `Delete this ${item.type}?`,
                            message: 'It will be permanently deleted.',
                            onConfirm: async () => {
                                if (item.type === 'image') {
                                    await DeleteAsset({assetId: item.id});
                                } else {
                                    await DeleteTrack({trackId: item.id});
                                }
                            },
                        });
                        ContextMenuStore.hide();
                    },
                },
            ];
            ContextMenuStore.show(event.clientX, event.clientY, limitedMenuItems);
            return;
        }

        const menuItems = [
            {
                title: "Rename",
                icon: () => <ModeEditIcon />,
                onClick: () => {
                    startRenaming(item);
                    ContextMenuStore.hide();
                },
            },
            {
                title: "Delete",
                icon: () => <DeleteIcon />,
                onClick: () => {
                    openDialog({
                        title: `Delete this ${item.type}?`,
                        message: 'It will be permanently deleted.',
                        onConfirm: async () => {
                            if (item.type === 'image') {
                                await DeleteAsset({assetId: item.id});
                            } else {
                                await DeleteTrack({trackId: item.id});
                            }
                        },
                    });
                    ContextMenuStore.hide();
                },
            },
            {
                title: "Download",
                icon: () => <DownloadIcon />,
                onClick: async () => {
                    ContextMenuStore.hide();
                    try {
                        let url: string | undefined;
                        const mimeHint = item.record?.mimetype || item.record?.mimeType || item.record?.contentType;
                        if (item.type === 'image') {
                            const asset = item.record;
                            if (asset?.assetId) {
                                url = await GetAssetUrl({ assetId: asset.assetId });
                            }
                        } else {
                            // audio/video track
                            const tr = item.record;
                            if (tr?.trackId) {
                                const trackId = tr.trackId;
                                url = await GetTrackUrl({ trackId });
                            }
                        }
                        if (url) {
                            await downloadFile(url, item.title, mimeHint);
                            return;
                        }
                        showDownloadError(item.title || 'download', 'Could not resolve a download URL.');
                    } catch (e) {
                        console.warn('Download setup failed', e);
                        const message = e instanceof Error ? e.message : 'Could not start download.';
                        showDownloadError(item.title || 'download', message);
                    }
                },
            },
        ];

        console.log("IS test user: ", authContext.isTestUser?.());

        // Add "Download enhanced" option for audio/video tracks, only for test users
        if ((item.type === 'audio' || item.type === 'video') && authContext.isTestUser?.()) {
            const tr = item.record;
            // Check if enhanced audio is available
            if (tr?.enhancedTrackState === 'ready') {
                menuItems.push({
                    title: "Download enhanced",
                    icon: () => <DownloadIcon />,
                    onClick: async () => {
                        ContextMenuStore.hide();
                        try {
                            const trackId = tr.trackId;
                            const url = await GetEnhancedAudioUrl({ trackId });
                            if (url) {
                                const enhancedName = item.title?.replace(/\.[^.]+$/, '_enhanced$&') || `${item.title}_enhanced`;
                                const mimeHint = tr?.mimetype || tr?.mimeType || tr?.contentType;
                                await downloadFile(url, enhancedName, mimeHint);
                                return;
                            }
                            showDownloadError(item.title || 'download', 'Enhanced audio is not available yet.');
                        } catch (e) {
                            console.warn('Download enhanced setup failed', e);
                            const message = e instanceof Error ? e.message : 'Could not start enhanced download.';
                            showDownloadError(item.title || 'download', message);
                        }
                    },
                });
            }
        }

        // Add "Re-transcribe" option for audio/video tracks only
        if (item.type === 'audio' || item.type === 'video') {
            menuItems.push({
                title: "Re-transcribe",
                icon: () => <TranscribeIcon />,
                onClick: () => {
                    openReTranscribeDialog(item.id, item.title);
                    ContextMenuStore.hide();
                },
            });
        }

        // Add "Move to Folder" submenu if there are folders
        if (folders.length > 0) {
            const currentFolderId = findFolderForFile(item.id);
            
            // Build folder options: all folders + "Root (no folder)" option
            const folderOptions = [
                // "Remove from folder" option if file is in a folder
                ...(currentFolderId ? [{
                    title: "Remove from folder",
                    icon: () => <DriveFileMoveOutlined />,
                    onClick: async () => {
                        await MoveFileToFolder({ fileId: item.id, fromFolderId: currentFolderId, toFolderId: undefined });
                        ContextMenuStore.hide();
                    },
                }] : []),
                // List all folders (except current)
                ...folders
                    .filter((f: any) => f.id !== currentFolderId)
                    .map((f: any) => ({
                        title: f.name || "Untitled Folder",
                        icon: () => <FolderOutlined />,
                        onClick: async () => {
                            await MoveFileToFolder({ fileId: item.id, fromFolderId: currentFolderId, toFolderId: f.id });
                            ContextMenuStore.hide();
                        },
                    })),
            ];

            if (folderOptions.length > 0) {
                menuItems.push({
                    title: "Move to...",
                    icon: () => <DriveFileMoveOutlined />,
                    children: folderOptions,
                } as any);
            }
        }

        ContextMenuStore.show(event.clientX, event.clientY, menuItems);
    };

	    const handleCompositionMenu = (event: React.MouseEvent, comp: any) => {
	        event.preventDefault();
	        event.stopPropagation();
        const compId = comp.id;
        const currentName = comp.title || comp.name || "";
        const menuItems = [
            {
                title: "Rename",
                icon: () => <ModeEditIcon />,
                onClick: () => {
                    setRenamingItemId(compId);
                    setRenameValue(currentName);
                    ContextMenuStore.hide();
                },
            },
            {
                title: "Duplicate",
                icon: () => <ContentCopyOutlinedIcon />,
                onClick: () => {
                    (async () => {
                        try {
                            const newId = await DuplicateComposition({ compositionId: compId });
                            const baseName = String(currentName || "Untitled").trim() || "Untitled";
                            setSelectedCompositionId(newId);
                            setSelectedCompositionTitle(`Copy of ${baseName}`);
                        } catch (e) {
                            console.error("[V2ProjectSidebar] Failed to duplicate composition", e);
                        }
                    })();
                    ContextMenuStore.hide();
                },
            },
            {
                title: "Delete",
                icon: () => <DeleteIcon />,
                onClick: () => {
                    openDialog({
                        title: `Delete this composition?`,
                        message: 'It will be permanently deleted.',
                        onConfirm: async () => {
                            try {
                                await DeleteComposition({ compositionId: compId });
                            } catch (_) {}
                        },
                    });
                    ContextMenuStore.hide();
                },
            },
        ];
	        ContextMenuStore.show(event.clientX, event.clientY, menuItems);
	    };

		    const tryReplacePendingPlaceholder = async (f: any): Promise<boolean> => {
		        const pending = ApplicationStore.peekPendingPlaceholderReplaceContext?.() ?? null;
		        if (!pending) return false;

		        const type = String(f?.type ?? "").toLowerCase();
		        if (type !== "video" && type !== "image") return false;

		        // Consume only when we're actually going to replace.
		        const consumed = ApplicationStore.consumePendingPlaceholderReplaceContext?.() ?? null;
		        const placeholderClipId = String(consumed?.placeholderClipId ?? "");
		        if (!placeholderClipId) return false;

		        const placeholder: any = CompositionService.getClipById(placeholderClipId);
		        if (!placeholder || String(placeholder?.type ?? "") !== "placeholder") return false;

		        const phStartSec = Number(placeholder?.start?.seconds ?? 0);
		        const phStopSec = Number(placeholder?.stop?.seconds ?? phStartSec);
		        if (!Number.isFinite(phStartSec) || !Number.isFinite(phStopSec) || phStopSec <= phStartSec) return false;
		        const phDuration = Math.max(0, phStopSec - phStartSec);
		        if (!(phDuration > 0)) return false;

		        const phLayerId = String(placeholder?.track?.id ?? "");
		        const compAny: any = CompositionService.composition as any;
		        const placeholderTrackIndex =
		            phLayerId && compAny && Array.isArray(compAny.tracks)
		                ? compAny.tracks.findIndex((t: any) => String(t?.id ?? "") === phLayerId)
		                : -1;

		        const phPos = (placeholder as any)?.position ?? { x: 0, y: 0 };
		        const phX = Number(phPos?.x ?? 0);
		        const phY = Number(phPos?.y ?? 0);
		        const phW = Number((placeholder as any)?.width ?? 0);
		        const phH = Number((placeholder as any)?.height ?? 0);
		        const phMask = String((placeholder as any)?.maskShape ?? "none");
		        const phName = String((placeholder as any)?.name ?? "");
		        const phAspectPreset = (placeholder as any)?.__librettoLayoutAspectPreset;

		        // Create a new media clip from the selected asset.
		        const thumbnailId = type === "image" ? f?.record?.thumbnailId : "";
		        const newClip: any = await CreateClipForComposition({
		            type,
		            thumbnailId,
		            assetId: f.id,
		            assetTitle: f?.record?.title,
		            assetResolution: f?.record?.resolution || "",
		            composition: CompositionService.composition,
		            stateStore: CompositionStateStore,
		            resolveAssetUrl: ResolveAssetUrl,
		        } as any);
		        if (!newClip) return false;

		        // Preserve layout slot identity so LayoutService doesn't recreate a placeholder.
		        try {
		            if (phName) (newClip as any).name = phName;
		        } catch {}
		        try {
		            if (typeof phAspectPreset === "string") (newClip as any).__librettoLayoutAspectPreset = phAspectPreset;
		        } catch {}

		        // Align to placeholder range on timeline.
		        try {
		            if ("range" in newClip && "offset" in newClip) {
		                const inSec = Number((newClip as any)?.range?.[0]?.seconds ?? 0);
		                (newClip as any).offset = Timestamp.fromSeconds(phStartSec - inSec);

		                const rate = Number((newClip as any)?.playbackRate ?? 1) || 1;
		                const desiredOutSrc = inSec + phDuration * rate;
		                let maxOutSrc = desiredOutSrc;
		                if (type !== "image") {
		                    const naturalSrcDur = Number(f?.record?.duration ?? f?.record?.Duration ?? NaN);
		                    if (Number.isFinite(naturalSrcDur) && naturalSrcDur > 0) {
		                        maxOutSrc = inSec + naturalSrcDur;
		                    }
		                }
		                const outSrc = Number.isFinite(maxOutSrc) ? Math.min(desiredOutSrc, maxOutSrc) : desiredOutSrc;
		                try {
		                    if (type === "image" && typeof (newClip as any)?.duration?.seconds === "number") {
		                        (newClip as any).duration.seconds = outSrc;
		                        (newClip as any).range[1].seconds = outSrc;
		                    } else {
		                        (newClip as any).range[1] = Timestamp.fromSeconds(outSrc);
		                    }
		                } catch {}
		            } else {
		                (newClip as any).start = Timestamp.fromSeconds(phStartSec);
		                (newClip as any).stop = Timestamp.fromSeconds(phStopSec);
		            }
		        } catch {}

		        // Adopt placeholder geometry (and mask shape) so it lands in the same place.
		        try {
		            if (Number.isFinite(phX) && Number.isFinite(phY)) (newClip as any).position = { x: phX, y: phY } as any;
		        } catch {}
		        try {
		            if (Number.isFinite(phW) && phW > 0) (newClip as any).width = phW as any;
		        } catch {}
		        try {
		            if (Number.isFinite(phH) && phH > 0) (newClip as any).height = phH as any;
		        } catch {}
		        try {
		            if (typeof (newClip as any).maskShape === "string") (newClip as any).maskShape = phMask as any;
		        } catch {}

			        // Copy scene overrides so it stays in the slot through scene edits.
			        try {
			            const ovsByAspect = (placeholder as any)?.sceneVisualOverridesByAspect;
			            const ovsLegacy = (placeholder as any)?.sceneVisualOverrides;
			            if (ovsByAspect && typeof ovsByAspect === "object") {
			                (newClip as any).sceneVisualOverridesByAspect = JSON.parse(JSON.stringify(ovsByAspect));
			            } else if (ovsLegacy && typeof ovsLegacy === "object") {
			                // Best-effort legacy fallback: apply to all aspect presets.
			                (newClip as any).sceneVisualOverridesByAspect = {
			                    horizontal: JSON.parse(JSON.stringify(ovsLegacy)),
			                    vertical: JSON.parse(JSON.stringify(ovsLegacy)),
			                    square: JSON.parse(JSON.stringify(ovsLegacy)),
			                };
			            }
			        } catch {}

		        // Remove the placeholder, then try to insert into the same track (if it still exists).
		        try {
		            CompositionService.deleteClipById(String(placeholderClipId));
		        } catch {}

		        let insertedTrack: any = null;
		        let addedToExistingLayer = false;
		        try {
		            const layerExists = phLayerId ? !!CompositionService.getLayerById(phLayerId) : false;
		            if (phLayerId && layerExists) {
		                insertedTrack = await (CompositionService.composition as any).add(newClip as any, phLayerId);
		                addedToExistingLayer = true;
		            }
		        } catch {}
		        if (!insertedTrack) {
		            try {
		                insertedTrack = await (CompositionService.composition as any).add(newClip as any);
		            } catch {
		                return false;
		            }
		        }

		        // If we had to create a new track, move it to the placeholder's z-order.
		        if (!addedToExistingLayer && placeholderTrackIndex >= 0 && insertedTrack && typeof insertedTrack.layer === "function") {
		            try {
		                const tracksNow: any[] = (CompositionService.composition as any)?.tracks ?? [];
		                const maxIndex = Math.max(0, tracksNow.length - 1);
		                const targetIndex = Math.max(0, Math.min(placeholderTrackIndex, maxIndex));
		                insertedTrack.layer(targetIndex);
		            } catch {}
		        }

		        try {
		            ApplicationStore.setSelectedClipId(String((newClip as any).id));
		        } catch {}

		        return true;
		    };

			    const handleAddToComposition = async (f: any) => {
			        const type = String(f?.type ?? "").toLowerCase();
			        const thumbnailId = type === "image" ? f?.record?.thumbnailId : "";

			        // If a placeholder is awaiting replacement, interpret this selection as "Add media".
			        if (await tryReplacePendingPlaceholder(f)) return;

		        const clipKind: "audio" | "visual" = type === "audio" ? "audio" : "visual";

		        // Best-effort source duration (seconds) from metadata; used for layer placement.
		        // If unknown, fall back to inserting on a new layer.
		        let naturalSrcDur = Number(f?.record?.duration ?? f?.record?.Duration ?? NaN);
		        if (!Number.isFinite(naturalSrcDur) || naturalSrcDur <= 0) {
		            if (type === "image") naturalSrcDur = 5;
		        }

			        const pendingInsert = ApplicationStore.consumePendingInsertIntoScriptContext?.() ?? null;
			        const pendingAddLayerFromSelection = pendingInsert
			            ? null
			            : (ApplicationStore.consumePendingAddLayerFromSelectionContext?.() ?? null);
			        const pending =
			            pendingInsert || pendingAddLayerFromSelection
			                ? null
			                : (ApplicationStore.consumePendingAddLayerContext?.() ?? null);

			        let forcedStartAt: number | null = null;
			        let forcedEndAt: number | null = null;
			        if (pendingInsert) {
			            try {
			                const scriptClip: any = CompositionService.getScriptLayerClip?.();
			                if (scriptClip && typeof scriptClip.getScenes === "function") {
			                    const EPS = 0.02;
			                    const desiredDurSec = Number.isFinite(naturalSrcDur) && naturalSrcDur > 0 ? naturalSrcDur : 5;
			                    const atClipLocalSec = Number(pendingInsert.atClipLocalSec ?? 0);
			                    if (Number.isFinite(atClipLocalSec) && desiredDurSec > 0) {
			                        const readScenes = (): any[] =>
			                            Array.isArray(scriptClip.getScenes?.()) ? scriptClip.getScenes() : [];
			                        const findSceneByStart = (t: number): any | null => {
			                            const scenes = readScenes();
			                            return (
			                                scenes.find((s: any) => Math.abs(Number(s?.startSec ?? NaN) - t) <= EPS) ??
			                                null
			                            );
			                        };
			                        const isEmptyScene = (scene: any): boolean => {
			                            const s0 = Number(scene?.startSec ?? NaN);
			                            const s1 = Number(scene?.endSec ?? NaN);
			                            if (!Number.isFinite(s0) || !Number.isFinite(s1) || s1 <= s0 + 1e-3) return false;
			                            const segsRaw: any = scriptClip?.scriptSegments;
			                            // If we can't confidently read segments, assume non-empty to avoid inserting over content.
			                            if (!Array.isArray(segsRaw) || segsRaw.length === 0) return false;
			                            const segs: any[] = segsRaw;
			                            return !segs.some((seg) => {
			                                const a0 = Number(seg?.srcStartSec ?? NaN);
			                                const a1 = Number(seg?.srcEndSec ?? NaN);
			                                if (!Number.isFinite(a0) || !Number.isFinite(a1) || a1 <= a0) return false;
			                                return a0 < s1 - 1e-3 && s0 < a1 - 1e-3;
			                            });
			                        };

			                        let scene = findSceneByStart(atClipLocalSec);
			                        const preferExisting = Boolean(pendingInsert.preferExistingEmptyScene);
			                        if (!(preferExisting && scene && isEmptyScene(scene))) {
			                            CompositionService.insertEmptyScriptScene(atClipLocalSec, desiredDurSec);
			                            scene = findSceneByStart(atClipLocalSec);
			                        }

			                        if (scene && isEmptyScene(scene)) {
			                            const currDur = Number(scene?.endSec ?? 0) - Number(scene?.startSec ?? 0);
			                            if (Number.isFinite(currDur) && desiredDurSec > currDur + EPS) {
			                                const diff = desiredDurSec - currDur;
			                                const endSec = Number(scene?.endSec ?? NaN);
			                                if (Number.isFinite(endSec) && diff > 0) {
			                                    CompositionService.insertEmptyScriptScene(endSec, diff);
			                                    // Merge adjacent empty scenes so we keep a single empty scene block.
			                                    scene = findSceneByStart(atClipLocalSec);
			                                    const idx = Number(scene?.index ?? NaN);
			                                    if (scene && Number.isFinite(idx) && idx >= 0) {
			                                        CompositionService.mergeScriptLayerSceneWithNext(idx);
			                                    }
			                                    scene = findSceneByStart(atClipLocalSec);
			                                }
			                            }

			                            const start = Number(scene?.timelineStartSec ?? NaN);
			                            if (Number.isFinite(start) && start >= 0) {
			                                forcedStartAt = start;
			                            }
			                        }
			                    }
			                }
			            } catch {}
			        }

			        if (pendingAddLayerFromSelection) {
			            try {
			                const startSec = Number(pendingAddLayerFromSelection.startSec ?? NaN);
			                const endSec = Number(pendingAddLayerFromSelection.endSec ?? NaN);
			                if (Number.isFinite(startSec) && Number.isFinite(endSec) && endSec > startSec) {
			                    // Create a scene spanning the selection (two splits). This is safe even if
			                    // a split already exists near the boundary (CompositionService dedupes).
			                    CompositionService.splitScriptLayer(startSec, true);
			                    CompositionService.splitScriptLayer(endSec, true);

			                    const mappedStart =
			                        ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(startSec) ??
			                        ScriptLayerStore.mapSourceTimeToCompositionTime(startSec);
			                    const mappedEnd =
			                        ScriptLayerStore.mapSourceTimeToCompositionTimeForBoundary(endSec) ??
			                        ScriptLayerStore.mapSourceTimeToCompositionTime(endSec);

			                    if (mappedStart != null && Number.isFinite(mappedStart)) {
			                        forcedStartAt = Math.max(0, Number(mappedStart));
			                    } else {
			                        const rate = Number(ScriptLayerStore.playbackRate || 1) || 1;
			                        forcedStartAt = Math.max(0, startSec / rate);
			                    }
			                    if (mappedEnd != null && Number.isFinite(mappedEnd)) {
			                        const end = Math.max(0, Number(mappedEnd));
			                        // Only constrain when the mapped end is strictly after the mapped start.
			                        if (forcedStartAt == null || end > forcedStartAt + 1e-3) {
			                            forcedEndAt = end;
			                        }
			                    } else {
			                        const rate = Number(ScriptLayerStore.playbackRate || 1) || 1;
			                        const end = Math.max(0, endSec / rate);
			                        if (forcedStartAt == null || end > forcedStartAt + 1e-3) {
			                            forcedEndAt = end;
			                        }
			                    }
			                }
			            } catch {}
			        }

			        const startAtRaw = forcedStartAt ?? pending?.startAtSec ?? (CompositionStateStore.currentTime ?? 0);
			        const startAt = Number.isFinite(Number(startAtRaw)) ? Math.max(0, Number(startAtRaw)) : 0;
			        const shouldConstrainToSceneEnd =
			            Boolean(pendingInsert) ||
			            Boolean(pendingAddLayerFromSelection) ||
			            Boolean(pending?.constrainToSceneEnd) ||
			            type === "image";

		        // If requested, constrain the inserted clip to the end of the scene that contains `startAt`.
		        // (Scene boundaries are script-clip local times; we map to timeline time for trimming/placement.)
		        let sceneEndTimeline: number | null = forcedEndAt;
		        if (shouldConstrainToSceneEnd && sceneEndTimeline == null) {
	            try {
	                const scriptClip: any = CompositionService.getScriptLayerClip?.();
	                if (scriptClip) {
                        // Prefer engine scene timing (timelineStartSec/timelineEndSec), which correctly handles
                        // inserted empty scenes (gap insertions) where timeline->source mapping returns null.
                        if (typeof scriptClip.getScenes === "function") {
                            const EPS = 1e-3;
                            const scenes: any[] = Array.isArray(scriptClip.getScenes()) ? scriptClip.getScenes() : [];
                            if (scenes.length > 0) {
                                const t = Number(startAt);
                                const match =
                                    scenes.find((s: any) => t >= Number(s?.timelineStartSec ?? 0) - EPS && t < Number(s?.timelineEndSec ?? 0) - EPS) ??
                                    (Math.abs(t - Number(scenes[scenes.length - 1]?.timelineEndSec ?? 0)) <= 0.02 ? scenes[scenes.length - 1] : null);
                                const end = match ? Number(match?.timelineEndSec ?? NaN) : NaN;
                                if (Number.isFinite(end) && end > t + EPS) {
                                    sceneEndTimeline = end;
                                }
                            }
                        }
                    }

	                if (scriptClip && sceneEndTimeline == null) {
	                    const EPS = 1e-3;
	                    const inSec = Number(scriptClip.range?.[0]?.seconds ?? 0);
	                    const outSec = Number(scriptClip.range?.[1]?.seconds ?? scriptClip.duration?.seconds ?? inSec);

	                    const sceneSplitsSrc: number[] = (scriptClip.splits ?? [])
	                        .filter((s: any) => s?.isSceneBoundary === true)
	                        .map((s: any) => Number(s.timeSec))
	                        .filter((t: any) => Number.isFinite(t) && t > inSec + EPS && t < outSec - EPS)
	                        .sort((a: number, b: number) => a - b);

	                    // Determine the clip-local time at the insertion point.
	                    let srcTime = ScriptLayerStore.mapTimelineTimeToSourceTime(startAt);
	                    if (srcTime == null) {
	                        // If we're inside an inserted gap, approximate by snapping to the next segment.
	                        const rate = Number(ScriptLayerStore.playbackRate || 1) || 1;
	                        const clipStartSec = Number(scriptClip.start?.seconds ?? 0);
	                        const absCompTime = clipStartSec + startAt * rate;
	                        const segs: any[] = Array.isArray(scriptClip.scriptSegments) ? scriptClip.scriptSegments : [];
	                        const nextSeg = segs.find((s: any) => Number(s?.compStartSec ?? 0) > absCompTime + EPS);
	                        if (nextSeg && Number.isFinite(nextSeg.srcStartSec)) {
	                            srcTime = Number(nextSeg.srcStartSec);
	                        }
	                    }

	                    if (srcTime != null && Number.isFinite(srcTime)) {
	                        const nextSplit = sceneSplitsSrc.find((t) => t > (srcTime as number) + EPS) ?? outSec;
	                        const mapped = ScriptLayerStore.mapSourceTimeToCompositionTime(nextSplit);
	                        if (mapped != null && Number.isFinite(mapped)) {
	                            sceneEndTimeline = Number(mapped);
	                        }
	                    }
	                }
	            } catch {}
	        }

	        // Images are timeline-native (no intrinsic duration); default them to fill the current scene.
	        if (type === "image" && shouldConstrainToSceneEnd && sceneEndTimeline != null && Number.isFinite(sceneEndTimeline)) {
	            naturalSrcDur = Math.max(0.25, (sceneEndTimeline as number) - startAt);
	        }

	        // Determine which existing layer (if any) can accept this clip at `startAt` without overlap.
	        // Choose the LOWEST compatible layer (bottom-most in the timeline UI).
	        let targetLayerId: string | null = null;
	        try {
	            const EPS = 1e-3;
	            const clipEndUnclamped = Number.isFinite(naturalSrcDur) && naturalSrcDur > 0 ? startAt + naturalSrcDur : NaN;
	            const clipEnd = sceneEndTimeline != null && Number.isFinite(sceneEndTimeline)
	                ? Math.min(clipEndUnclamped, sceneEndTimeline)
	                : clipEndUnclamped;

	            if (Number.isFinite(clipEnd) && clipEnd > startAt + EPS) {
	                const scriptLayerId = (CompositionService.composition as any)?.scriptLayer?.layerId ?? null;
	                const allTopToBottom = [...(CompositionStateStore.orderedLayerIds ?? [])].reverse();
	                const regularTopToBottom = scriptLayerId ? allTopToBottom.filter((id) => id !== scriptLayerId) : allTopToBottom;

	                const isCompatibleTrack = (track: any): boolean => {
	                    let hasAudio = false;
	                    let hasVisual = false;
	                    for (const c of track?.clips ?? []) {
	                        if (!c) continue;
	                        const t = String((c as any).type ?? "");
	                        if (t === "audio") hasAudio = true;
	                        if (CompositionService.isVisualClip(c)) hasVisual = true;
	                    }
	                    // Don't try to pack into mixed tracks (shouldn't exist under invariants).
	                    if (hasAudio && hasVisual) return false;
	                    if (clipKind === "audio") return !hasVisual;
	                    return !hasAudio;
	                };

	                const fitsWithoutOverlap = (track: any): boolean => {
	                    for (const c of track?.clips ?? []) {
	                        if (!c) continue;
	                        if (String((c as any).type ?? "") === "script") continue;
	                        const s = Number((c as any).start?.seconds ?? 0);
	                        const d = Number((c as any).timelineDuration?.seconds ?? 0);
	                        const e = s + d;
	                        if (!Number.isFinite(s) || !Number.isFinite(e)) continue;
	                        if (startAt < e - EPS && s < clipEnd - EPS) return false;
	                    }
	                    return true;
	                };

	                for (let i = regularTopToBottom.length - 1; i >= 0; i--) {
	                    const layerId = regularTopToBottom[i];
	                    const track: any = CompositionService.getLayerById(layerId);
	                    if (!track) continue;
	                    if (!isCompatibleTrack(track)) continue;
	                    if (!fitsWithoutOverlap(track)) continue;
	                    targetLayerId = layerId;
	                    break;
	                }
	            }
	        } catch {}

	        const clip = await CreateClipForComposition({type: f.type as string, thumbnailId: thumbnailId, assetId: f.id, assetTitle: f?.record?.title, assetResolution: f?.record?.resolution || "", composition: CompositionService.composition, stateStore: CompositionStateStore, resolveAssetUrl: ResolveAssetUrl});
	        if (!clip) return;

	        // Place at the desired time by setting offset BEFORE inserting (avoid moveClipTo clamping).
	        try {
	            if ("range" in clip && "offset" in clip) {
	                const inSec = Number((clip as any)?.range?.[0]?.seconds ?? 0);
	                (clip as any).offset = Timestamp.fromSeconds(startAt - inSec);
	            }
	        } catch {}

	        // Images can be extended arbitrarily; set their duration to fill the current scene by default.
	        if (type === "image" && shouldConstrainToSceneEnd && sceneEndTimeline != null && Number.isFinite(sceneEndTimeline)) {
	            try {
	                const EPS = 1e-3;
	                const minTimelineDur = 0.25;
	                const rate = Number((clip as any)?.playbackRate ?? 1) || 1;
	                const inSec = Number((clip as any)?.range?.[0]?.seconds ?? 0);
	                const maxTimelineDur = Math.max(minTimelineDur, (sceneEndTimeline as number) - startAt);
	                const desiredOutSrc = inSec + maxTimelineDur * rate;
	                if (Number.isFinite(desiredOutSrc) && desiredOutSrc > inSec + EPS) {
	                    try { (clip as any).duration.seconds = desiredOutSrc; } catch {}
	                    try { (clip as any).range[1].seconds = desiredOutSrc; } catch {}
	                }
	            } catch {}
	        }

	        // If we know the media duration, pre-trim to the scene end so track insertion never
	        // tries to "resolve" overlap with clips that start in later scenes.
	        if (shouldConstrainToSceneEnd && sceneEndTimeline != null && Number.isFinite(sceneEndTimeline) && Number.isFinite(naturalSrcDur) && naturalSrcDur > 0) {
	            try {
	                const EPS = 1e-3;
	                const rate = Number((clip as any)?.playbackRate ?? 1) || 1;
	                const inSec = Number((clip as any)?.range?.[0]?.seconds ?? 0);
	                const naturalOutSrc = inSec + naturalSrcDur;
	                const maxTimelineDur = Math.max(0, (sceneEndTimeline as number) - startAt);
	                const naturalTimelineDur = naturalSrcDur / rate;
	                const desiredTimelineDur = Math.min(naturalTimelineDur, maxTimelineDur);
	                const desiredOutSrc = inSec + desiredTimelineDur * rate;
	                if (Number.isFinite(desiredOutSrc) && desiredOutSrc > inSec + EPS && desiredOutSrc < naturalOutSrc - EPS) {
	                    (clip as any).range[1] = Timestamp.fromSeconds(desiredOutSrc);
	                }
	            } catch {}
	        }

	        if (targetLayerId) {
	            await CompositionService.composition.add(clip, targetLayerId);
	        } else {
	            await CompositionService.composition.add(clip);
	        }

	        // Ensure the inserted clip does not extend past the current scene end.
	        if (shouldConstrainToSceneEnd && sceneEndTimeline != null && Number.isFinite(sceneEndTimeline)) {
	            try {
	                const added: any = CompositionService.getClipById(clip.id);
	                const clipStart = Number(added?.start?.seconds ?? startAt);
	                const clipDur = Number(added?.timelineDuration?.seconds ?? 0);
	                const clipEnd = clipStart + clipDur;
	                const EPS = 1e-3;
	                if (Number.isFinite(clipEnd) && clipEnd > (sceneEndTimeline as number) + EPS) {
	                    CompositionService.trimClipRight(clip.id, (sceneEndTimeline as number) - clipEnd);
	                }
	            } catch {}
	        }

	        trackSessionEvent("clip_added");
	    }

    const handleInsertIntoScript = async (f: any) => {
        const clip = await CreateClipForComposition({type: f.type as string, assetId: f.id, assetTitle: f?.record?.title, assetResolution: f?.record?.resolution || "", composition: CompositionService.composition, stateStore: CompositionStateStore, resolveAssetUrl: ResolveAssetUrl});
        if (!clip) return;
        await CompositionService.addToScriptLayer(clip);
    };

    const handleInsertIntoScriptLayer = async (f: any) => {
        try {
            const type = String(f.type).toLowerCase();
            const assetId = f.id;
            const assetTitle = f?.record?.title;
            const assetResolution = f?.record?.resolution || '';

            if (!assetId) {
                console.warn('[handleInsertIntoScriptLayer] No asset ID provided');
                return;
            }

            // Resolve the asset URL to create a MediaBunny Input
            const isVideo = type === 'video';
            const kind: 'audio' | 'video' = isVideo ? 'video' : 'audio';
            const url = await ResolveAssetUrl(assetId, kind);
            if (!url) {
                console.warn('[handleInsertIntoScriptLayer] Could not resolve URL for asset:', assetId);
                return;
            }

            const source = new UrlSource(url);
            const input = new Input({ source, formats: ALL_FORMATS });

            // Parse video dimensions from resolution string
            const resolutionMatch = assetResolution.match(/(\d+)x(\d+)/);
            let trackWidth = 0;
            let trackHeight = 0;
            if (resolutionMatch && isVideo) {
                trackWidth = parseInt(resolutionMatch[1]);
                trackHeight = parseInt(resolutionMatch[2]);
            }

	            // Get composition dimensions for positioning
	            const comp = CompositionService.composition;
	            if (isVideo && trackWidth > 0 && trackHeight > 0) {
	                maybeLockCanvasToFirstVideo(comp, trackWidth, trackHeight);
	            }
	            const compWidth = comp?.width ?? 1920;
	            const compHeight = comp?.height ?? 1080;

            // Build the input config
            // Note: name is intentionally left empty so speaker shows as "New Speaker" placeholder
            // This prompts users to explicitly assign speakers rather than auto-using filenames
            const config: ScriptLayerInputConfig = {
                input,
                assetId,  // For waveform lookup
                name: '',  // Empty - will show "New Speaker" placeholder
                color: '#3B82F6',  // Default blue color
                gain: 1.0,
            };

            // Add visual properties if this is a video
            if (isVideo) {
                const visualDims = calculateVideoVisualDimensions(
                    trackWidth, trackHeight, compWidth, compHeight
                );
                config.visual = {
                    x: visualDims.x,
                    y: visualDims.y,
                    width: visualDims.width,
                    height: visualDims.height,
                    alpha: 1.0,
                };
            }

            // Create script clip using shared utility
            const scriptLayerClip = await CreateScriptClipForComposition({
                inputConfigs: [config],
                composition: comp,
                stateStore: CompositionStateStore,
                clipName: assetTitle,
            });

            if (!scriptLayerClip) {
                console.warn('[handleInsertIntoScriptLayer] Failed to create script clip');
                return;
            }

            // Set additional metadata
            CompositionStateStore.setOriginalMediaResolutionForClip(scriptLayerClip.id, trackWidth, trackHeight);

            // Add to script layer
            await CompositionService.addToScriptLayer(scriptLayerClip);

            console.log('[handleInsertIntoScriptLayer] Added script layer clip:', scriptLayerClip.id);
        } catch (e) {
            console.error('[handleInsertIntoScriptLayer] Failed to create script layer clip:', e);
        }
    }

	    const handleInsertSequenceIntoScript = async (sequenceId: string) => {
	        try {
	            // Initialize persistence service temporarily to fetch sequence clips
	            const userId = authContext.getUserId();
	            SequencePersistenceService.init(userId, sequenceId, authContext.getCurrentWorkspaceId?.());

            // Fetch sequence clips from storage
            const { layers: layersWithClips } = await SequencePersistenceService.fetchClipsByLayerOrdered(sequenceId);
            
            // Collect all clips from all layers
            const allClips: any[] = [];
            for (const layerData of layersWithClips) {
                allClips.push(...layerData.clips);
            }

            if (allClips.length === 0) {
                console.warn('[V2ProjectSidebar] Sequence has no clips to insert');
                SequencePersistenceService.reset();
                return;
            }

	            // Get composition dimensions
	            const comp = CompositionService.composition;
	            // If this is the first video being added to the composition, lock the canvas to it.
	            try {
	                const firstVideoRec = allClips.find((r: any) => String(r?.type ?? '').toLowerCase() === 'video');
	                if (firstVideoRec) {
	                    let vw = Number(firstVideoRec?.props?.width ?? 0);
	                    let vh = Number(firstVideoRec?.props?.height ?? 0);
	                    if (!(vw > 0 && vh > 0)) {
	                        const trackRecord = ProjectAssetsStore.projectTracks.find(
	                            (t: any) => t.id === firstVideoRec.assetId || t.trackId === firstVideoRec.assetId
	                        );
	                        const resMat = String(trackRecord?.resolution ?? '').match(/(\d+)x(\d+)/);
	                        if (resMat) {
	                            vw = parseInt(resMat[1], 10);
	                            vh = parseInt(resMat[2], 10);
	                        }
	                    }
	                    if (vw > 0 && vh > 0) {
	                        maybeLockCanvasToFirstVideo(comp, vw, vh);
	                    }
	                }
	            } catch {}
	            const compWidth = comp?.width ?? 1920;
	            const compHeight = comp?.height ?? 1080;

            // Build ScriptLayerInputConfig[] per SEQUENCE layer.
            // Each sequence track/layer becomes a ScriptClip source with multi-segment placement.
            const inputConfigs: ScriptLayerInputConfig[] = [];
            const genSegId = () => crypto?.randomUUID?.() ?? `seg_${Date.now()}_${Math.random()}`;

            for (const layerData of layersWithClips) {
                const layerClips: any[] = Array.isArray(layerData?.clips) ? layerData.clips : [];
                if (layerClips.length === 0) continue;

                // Resolve the source name from the sequence layer (track label).
                let layerName = String(layerData?.name ?? "").trim();
                if (!layerName) {
                    // Fallback to first clip name, then project track title.
                    const firstClip = layerClips.find((c: any) => c?.assetId) ?? layerClips[0];
                    layerName = String(firstClip?.name ?? "").trim();
                    if (!layerName) {
                        const firstAssetId = firstClip?.assetId;
                        const trackRecord = ProjectAssetsStore.projectTracks.find(
                            (t: any) => t.id === firstAssetId || t.trackId === firstAssetId
                        );
                        layerName = String(trackRecord?.title || trackRecord?.name || "").trim();
                    }
                }

                // Group clips by assetId+type (best-effort backstop for layers containing multiple assets).
                const clipsByKey = new Map<string, any[]>();
                for (const rec of layerClips) {
                    const assetId = rec?.assetId;
                    if (!assetId) continue;
                    const type = String(rec?.type ?? "audio").toLowerCase();
                    const key = `${assetId}::${type}`;
                    const cur = clipsByKey.get(key) ?? [];
                    cur.push(rec);
                    clipsByKey.set(key, cur);
                }

                for (const [key, recs] of clipsByKey.entries()) {
                    try {
                        const firstRec: any = recs[0];
                        const assetId = firstRec?.assetId;
                        if (!assetId) continue;

                        const type = String(firstRec?.type ?? "audio").toLowerCase();
                        const isVideo = type === "video";
                        const kind: "audio" | "video" | "image" = isVideo ? "video" : "audio";

                        const url = await ResolveAssetUrl(assetId, kind);
                        if (!url) continue;

                        const source = new UrlSource(url);
                        const input = new Input({ source, formats: ALL_FORMATS });

                        // Get the waveform color (prefer persisted waveformColor on the sequence clip).
                        const sourceColor =
                            firstRec?.waveformColor ||
                            DEFAULT_SPEAKER_COLORS[inputConfigs.length % DEFAULT_SPEAKER_COLORS.length];

                        // Build per-clip segments on the ScriptClip spine.
                        const segments = recs
                            .map((rec: any) => {
                                const offsetSec = Number(rec?.startSec ?? 0);
                                const inSec = Number(rec?.inSec ?? 0);
                                const outSec = Number(rec?.outSec ?? 0);
                                if (!Number.isFinite(offsetSec) || offsetSec < 0) return null;
                                if (!Number.isFinite(inSec) || !Number.isFinite(outSec) || outSec <= inSec) return null;

                                const seg: any = {
                                    id: genSegId(),
                                    offsetSec,
                                    range: { inSec, outSec },
                                };
                                const segRate = Number(rec?.props?.playbackRate ?? 1.0);
                                if (Number.isFinite(segRate) && segRate > 0 && segRate !== 1.0) {
                                    seg.playbackRate = segRate;
                                }
                                return seg;
                            })
                            .filter(Boolean)
                            .sort((a: any, b: any) => Number(a.offsetSec ?? 0) - Number(b.offsetSec ?? 0));

                        if (segments.length === 0) continue;

                        const config: ScriptLayerInputConfig = {
                            input,
                            assetId,
                            name: layerName,
                            color: sourceColor,
                            gain: firstRec?.props?.gain !== undefined ? Number(firstRec.props.gain) : 1.0,
                            segments,
                        };

                        if (kind === "video") {
                            // Get original video dimensions from the track record
                            let videoWidth = firstRec?.props?.width ?? 0;
                            let videoHeight = firstRec?.props?.height ?? 0;

                            if (!videoWidth || !videoHeight) {
                                const trackRecord = ProjectAssetsStore.projectTracks.find(
                                    (t: any) => t.id === assetId || t.trackId === assetId
                                );
                                const resMat = String(trackRecord?.resolution ?? "").match(/(\d+)x(\d+)/);
                                if (resMat) {
                                    videoWidth = parseInt(resMat[1], 10);
                                    videoHeight = parseInt(resMat[2], 10);
                                }
                            }

                            const visualDims = calculateVideoVisualDimensions(videoWidth, videoHeight, compWidth, compHeight);
                            config.visual = {
                                x: visualDims.x,
                                y: visualDims.y,
                                width: visualDims.width,
                                height: visualDims.height,
                                alpha: 1.0,
                            };
                        }

                        inputConfigs.push(config);
                    } catch (e) {
                        console.error("[V2ProjectSidebar] Failed to create input config from sequence layer", { key }, e);
                    }
                }
            }

	            if (inputConfigs.length === 0) {
	                console.warn('[V2ProjectSidebar] No input configs could be created from sequence');
	                SequencePersistenceService.reset();
	                return;
	            }

	            // If a ScriptLayerClip spine already exists, insert this sequence at the playhead instead of
	            // replacing the whole script clip. This supports "insert into script" and later copy/paste.
	            try {
	                const existingScript: any = CompositionService.getScriptLayerClip?.();
	                if (existingScript && String(existingScript.type) === "script") {
	                    const computeSeqDurationSec = (cfgs: ScriptLayerInputConfig[]): number => {
	                        let maxEnd = 0;
	                        for (const cfg of cfgs) {
	                            const cfgRate = Number((cfg as any)?.playbackRate ?? 1.0) || 1.0;
	                            const segs: any[] = Array.isArray((cfg as any)?.segments) ? ((cfg as any).segments as any[]) : [];
	                            for (const seg of segs) {
	                                const off = Number(seg?.offsetSec ?? 0);
	                                const pr = Number(seg?.playbackRate ?? cfgRate) || 1.0;
	                                const rin = Number(seg?.range?.inSec ?? NaN);
	                                const rout = Number(seg?.range?.outSec ?? NaN);
	                                if (!Number.isFinite(off) || off < 0) continue;
	                                if (!Number.isFinite(pr) || pr <= 0) continue;
	                                if (!Number.isFinite(rin) || !Number.isFinite(rout) || rout <= rin) continue;
	                                const dur = (rout - rin) / pr;
	                                maxEnd = Math.max(maxEnd, off + dur);
	                            }
	                        }
	                        return maxEnd;
	                    };

	                    const seqDurSec = computeSeqDurationSec(inputConfigs);
	                    if (!Number.isFinite(seqDurSec) || seqDurSec <= 1e-3) {
	                        console.warn("[handleInsertSequenceIntoScript] Sequence duration is invalid; aborting insert", { seqDurSec });
	                        SequencePersistenceService.reset();
	                        return;
	                    }

	                    const absCompTime = Number(CompositionStateStore.currentTime ?? CompositionService.getCurrentTime() ?? 0);
	                    let atClipLocalSec: number | null = null;
	                    try {
	                        if (typeof existingScript.mapCompTimeToClipLocal === "function") {
	                            atClipLocalSec = existingScript.mapCompTimeToClipLocal(absCompTime);
	                        }
	                    } catch {}
	                    if (atClipLocalSec == null) {
	                        try {
	                            // Fallback to store mapping (timeline-relative seconds).
	                            atClipLocalSec = ScriptLayerStore.mapTimelineTimeToSourceTime(absCompTime);
	                        } catch {}
	                    }
	                    if (atClipLocalSec == null || !Number.isFinite(atClipLocalSec)) {
	                        // Conservative fallback: insert at clip end.
	                        const inSec = Number(existingScript.range?.[0]?.seconds ?? 0);
	                        const outSec = Number(existingScript.range?.[1]?.seconds ?? existingScript.duration?.seconds ?? inSec);
	                        atClipLocalSec = Number.isFinite(outSec) ? outSec : inSec;
	                    }

	                    // 1) Create "fillable spine time" (empty scene) to make room.
	                    const ok = CompositionService.insertEmptyScriptScene(Number(atClipLocalSec), Number(seqDurSec));
	                    if (!ok) {
	                        console.warn("[handleInsertSequenceIntoScript] Failed to insert empty scene for sequence insert");
	                        SequencePersistenceService.reset();
	                        return;
	                    }

	                    // 2) Merge sources + append shifted segments into the new empty region.
	                    const existingCfgs: any[] =
	                        typeof existingScript.getInputConfigs === "function" ? (existingScript.getInputConfigs() as any[]) : [];
	                    const hasAsset = (assetId: string): boolean =>
	                        existingCfgs.some((c: any) => String(c?.assetId ?? "") === String(assetId ?? ""));

	                    const shiftedSegmentsFor = (cfg: ScriptLayerInputConfig): any[] => {
	                        const segs: any[] = Array.isArray((cfg as any)?.segments) ? ((cfg as any).segments as any[]) : [];
	                        return segs
	                            .map((seg: any) => {
	                                const off = Number(seg?.offsetSec ?? 0);
	                                const rin = Number(seg?.range?.inSec ?? NaN);
	                                const rout = Number(seg?.range?.outSec ?? NaN);
	                                if (!Number.isFinite(off) || off < 0) return null;
	                                if (!Number.isFinite(rin) || !Number.isFinite(rout) || rout <= rin) return null;
	                                const rec: any = {
	                                    id: seg?.id ? String(seg.id) : genSegId(),
	                                    offsetSec: off + Number(atClipLocalSec),
	                                    range: { inSec: rin, outSec: rout },
	                                };
	                                const pr = seg?.playbackRate;
	                                if (Number.isFinite(pr) && Number(pr) > 0) rec.playbackRate = Number(pr);
	                                return rec;
	                            })
	                            .filter(Boolean)
	                            .sort((a: any, b: any) => Number(a.offsetSec ?? 0) - Number(b.offsetSec ?? 0));
	                    };

	                    for (const cfg of inputConfigs) {
	                        const assetId = String((cfg as any)?.assetId ?? "").trim();
	                        if (!assetId) continue;
	                        const segs = shiftedSegmentsFor(cfg);
	                        if (segs.length === 0) continue;

	                        if (hasAsset(assetId)) {
	                            try {
	                                (existingScript as any).appendSourceSegments?.(assetId, segs);
	                            } catch {}
	                            continue;
	                        }

	                        // New source: append config to the existing ScriptLayerClip (Option B).
	                        try {
	                            (cfg as any).segments = segs;
	                            await (existingScript as any).appendInputConfig?.(cfg);
	                        } catch (e) {
	                            console.warn("[handleInsertSequenceIntoScript] Failed to append new script source", { assetId }, e);
	                        }
	                    }

	                    // 3) Rebuild schedule + notify stores/persistence/thumbnails.
	                    try {
	                        await CompositionService.composition.onClipTimingChanged();
	                    } catch {}
	                    try {
	                        CompositionService.notifyScriptLayerUpdated({}, { timingChanged: false });
	                    } catch {}
	                    try {
	                        CompositionService.composition.computeFrame();
	                    } catch {}

	                    SequencePersistenceService.reset();
	                    return;
	                }
	            } catch {}

	            // Create speakers in Firestore for each source that has a name
	            // This makes speakers available for the speaker picker in the transcript editor
	            const projectId = ProjectAssetsStore.projectId;
            const speakerIdBySourceIndex = new Map<number, string>();
            if (projectId) {
                for (let sourceIndex = 0; sourceIndex < inputConfigs.length; sourceIndex++) {
                    const config = inputConfigs[sourceIndex];
                    const name = config.name?.trim() ?? "";
                    if (name) {
                        // Check if speaker with this name already exists
                        const existingSpeaker = ProjectAssetsStore.projectSpeakers.find(
                            (s: any) => String(s.name ?? "").toLowerCase() === name.toLowerCase()
                        );
                        
                        if (!existingSpeaker) {
                            try {
                                const speakerId = await CreateSpeaker({ projectId, name });
                                console.log('[V2ProjectSidebar] Created speaker for source:', name, speakerId);
                                if (speakerId) speakerIdBySourceIndex.set(sourceIndex, speakerId);
                            } catch (e) {
                                console.warn('[V2ProjectSidebar] Failed to create speaker:', name, e);
                            }
                        } else {
                            const speakerId = String(existingSpeaker.speakerId ?? existingSpeaker.id ?? "");
                            if (speakerId) speakerIdBySourceIndex.set(sourceIndex, speakerId);
                            console.log('[V2ProjectSidebar] Speaker already exists:', name, speakerId);
                        }
                    }
                }
            }

            // Get sequence name for the clip
            const sequenceName = ProjectAssetsStore.projectSequences.find((s: any) => s.id === sequenceId)?.title || "Sequence";

            // Create script clip using shared utility
            const scriptLayerClip = await CreateScriptClipForComposition({
                inputConfigs,
                composition: comp,
                stateStore: CompositionStateStore,
                clipName: sequenceName,
            });

            if (!scriptLayerClip) {
                console.warn('[handleInsertSequenceIntoScript] Failed to create script clip');
                SequencePersistenceService.reset();
                return;
            }

            // For ScriptClips created from sequences, assign ONE Firestore Speaker per source track.
            // This collapses diarization speakerIds within a single source to one speaker.
            try {
                const endSec = Number(scriptLayerClip.duration?.seconds ?? scriptLayerClip.range?.[1]?.seconds ?? 0);
                if (Number.isFinite(endSec) && endSec > 0) {
                    const overrides: SpeakerOverride[] = [];
                    for (let sourceIndex = 0; sourceIndex < inputConfigs.length; sourceIndex++) {
                        const config = inputConfigs[sourceIndex];
                        const name = config.name?.trim() ?? "";
                        if (!name) continue;
                        const speakerId = speakerIdBySourceIndex.get(sourceIndex);
                        overrides.push({
                            id: crypto?.randomUUID?.() ?? `spk_${Date.now()}_${Math.random()}`,
                            startSec: 0,
                            endSec,
                            sourceIndex,
                            speakerName: name,
                            color: typeof config.color === "string" ? config.color : undefined,
                            speakerId: speakerId,
                        });
                    }
                    if (overrides.length > 0) {
                        scriptLayerClip.applyPersistedEdits({ speakerOverrides: overrides });
                    }
                }
            } catch {}

            console.log('[handleInsertSequenceIntoScript] Before add - start:', scriptLayerClip.start?.seconds, 'stop:', scriptLayerClip.stop?.seconds);

            // Add to script layer
            await CompositionService.addToScriptLayer(scriptLayerClip);
            
            console.log('[handleInsertSequenceIntoScript] After add - track:', scriptLayerClip.track?.id);

            // Clean up
            SequencePersistenceService.reset();
        } catch (e) {
            console.error('[V2ProjectSidebar] Failed to insert sequence into script', e);
            SequencePersistenceService.reset();
        }
    };

    const isTrackFile = (f: any): boolean => String(f?.type ?? "") === "audio" || String(f?.type ?? "") === "video";

    const openPreviewForFile = async (e: React.MouseEvent, f: any) => {
        setPreviewMode("single");
        setPreviewAnchorEl(e.currentTarget as HTMLElement);
        setPreviewItem(f);
    };

    const openPreviewForSelection = (e: React.MouseEvent) => {
        setPreviewMode("multi");
        setPreviewAnchorEl(e.currentTarget as HTMLElement);
        setPreviewItem(null);
    };

    const handleFileRowClick = async (
        e: React.MouseEvent,
        f: any,
        options?: { allowPendingPlaceholderReplace?: boolean },
    ) => {
        const fileReady = isFileReady(f);
        if (!fileReady) return;

        const pending = ApplicationStore.peekPendingPlaceholderReplaceContext?.() ?? null;
        if (
            options?.allowPendingPlaceholderReplace &&
            pending &&
            (String(f?.type ?? "").toLowerCase() === "video" || String(f?.type ?? "").toLowerCase() === "image")
        ) {
            void tryReplacePendingPlaceholder(f);
            return;
        }

        const isModifierClick = Boolean((e as any).metaKey || (e as any).ctrlKey);
        const track = isTrackFile(f);

        if (isModifierClick && track) {
            let nextSize = 0;
            setSelectedTrackIds((prev) => {
                const next = new Set(prev);
                if (next.has(f.id)) {
                    next.delete(f.id);
                } else {
                    next.add(f.id);
                }
                nextSize = next.size;
                return next;
            });

            if (nextSize > 1) {
                openPreviewForSelection(e);
            } else {
                setPreviewAnchorEl(null);
                setPreviewItem(null);
                setPreviewMode("single");
            }
            return;
        }

        setSelectedTrackIds(track ? new Set([String(f.id)]) : new Set());
        await openPreviewForFile(e, f);
    };

    const handleClosePreview = () => {
        setPreviewAnchorEl(null);
        setPreviewItem(null);
        setPreviewMode("single");
    };

    const openPreviewForSequence = (e: React.MouseEvent, s: any) => {
        setSequencePopoverAnchorEl(e.currentTarget as HTMLElement);
        setSequencePopoverItem(s);
    };

    const handleCloseSequencePopover = () => {
        setSequencePopoverAnchorEl(null);
        setSequencePopoverItem(null);
    };

    const openReTranscribeDialog = (trackId: string, trackTitle: string) => {
        setReTranscribeTrackInfo({ id: trackId, title: trackTitle });
        setReTranscribeDialogOpen(true);
    };

    const handleReTranscribe = useCallback(async (trackId: string, language: string, numSpeakers: number | 'auto', skipDenoising: boolean) => {
        const success = await RetranscribeTrack({ trackId, language, numSpeakers, skipDenoising });
        if (success) {
            console.log('[V2ProjectSidebar] Re-transcription initiated for track:', trackId, 'skipDenoising:', skipDenoising);
        }
    }, [RetranscribeTrack]);

    const openBackupDialog = (trackTitle: string, backupTrackId: string) => {
        setBackupTrackInfo({ title: trackTitle, backupTrackId });
        setBackupDialogOpen(true);
    };

    const handlePromoteBackup = useCallback(async (backupTrackId: string) => {
        const success = await PromoteBackupTrack({ backupTrackId });
        if (success) {
            console.log('[V2ProjectSidebar] Backup promotion initiated for backup track:', backupTrackId);
        }
    }, [PromoteBackupTrack]);

    return (
        <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', p: '3px', position: 'relative' }}>
            <ConfirmDialogComponent/>
            <BackupPromotionDialog
                isOpen={backupDialogOpen}
                onClose={() => setBackupDialogOpen(false)}
                trackTitle={backupTrackInfo?.title || ''}
                backupTrackId={backupTrackInfo?.backupTrackId || ''}
                onPromote={handlePromoteBackup}
            />
            <ReTranscribeDialog
                isOpen={reTranscribeDialogOpen}
                onClose={() => setReTranscribeDialogOpen(false)}
                trackTitle={reTranscribeTrackInfo?.title || ''}
                trackId={reTranscribeTrackInfo?.id || ''}
                onTranscribe={handleReTranscribe}
            />
            <Box sx={{display: 'flex', flexDirection: 'row', alignItems: 'flex-start', marginLeft: '8px'}}>
                <Box sx={{width: '24px', height: '24px', color: 'var(--app-text-primary)'}}>
                    <FilmIcon/>
                </Box>
                <Box sx={titleTextStyle}>Project</Box>
            </Box>

            {/* Compositions (auto height) */}
            <PaperPanel
                title="Compositions"
                addButtonDataTour="project-compositions-add"
                onAdd={async () => {
                    const newCompositionId = await CreateComposition({ projectId: ProjectAssetsStore.projectId });
                    if (!newCompositionId) return;
                    setSelectedCompositionId?.(newCompositionId);
                    setSelectedCompositionTitle?.("Untitled");
                }}
            >
                <List dense sx={{ py: 0 }}>
                    {compositions.map((c: any) => {
                        if (renamingItemId === c.id) {
                            return (
                                <ListItem key={c.id} disablePadding>
                                    <ListItemButton sx={{ py: 0.75, pr: 4, pl: 3 }} onClick={(e) => e.stopPropagation()}>
                                        <ListItemIcon sx={{ minWidth: 32, mr: 1 }}>
                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'text.secondary' }} />
                                        </ListItemIcon>
                                        <TextField
                                            value={renameValue}
                                            onChange={(e) => setRenameValue(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onBlur={() => {
                                                (async () => {
                                                    const newName = renameValue.trim();
                                                    if (newName && newName !== (c.title || c.name || "")) {
                                                        try {
                                                            await RenameComposition({ compositionId: c.id, newName });
                                                        } catch (_) {}
                                                    }
                                                    setRenamingItemId(null);
                                                })();
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    (async () => {
                                                        const newName = renameValue.trim();
                                                        if (newName && newName !== (c.title || c.name || "")) {
                                                            try {
                                                                await RenameComposition({ compositionId: c.id, newName });
                                                            } catch (_) {}
                                                        }
                                                        setRenamingItemId(null);
                                                    })();
                                                } else if (e.key === 'Escape') {
                                                    setRenamingItemId(null);
                                                    setRenameValue("");
                                                }
                                            }}
                                            autoFocus
                                            size="small"
                                            variant="standard"
                                            sx={{ flex: 1, '& .MuiInput-input': { fontSize: '13px', padding: '0px' } }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        }
                        return (
                            <RowItem
                                key={c.id}
                                icon={
                                    <Box sx={{ position: 'relative', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent' }}>
                                        {selectedCompositionId === c.id && (
                                            <CheckmarkIcon/>
                                        )}
                                    </Box>
                                }
                                iconBgColor={'transparent'}
                                primary={c.title || c.name || 'Untitled'}
                                onClick={() => {
                                    setSelectedCompositionId(c.id);
                                    setSelectedCompositionTitle(c.title);
                                }}
                                onMenu={(e) => handleCompositionMenu(e, c)}
                            />
                        );
                    })}
                </List>
            </PaperPanel>

            {/* Files (fills remaining space) */}
            <PaperPanel
                title="Files"
                onAdd={(anchor) => setAddMenuAnchor(anchor ?? null)}
                fill
                highlight={Boolean(highlightFilesPanel)}
                dataTour="project-files-panel"
            >
                <List dense sx={{ py: 0 }}>
                    {/* Sequences group */}
                    {sequences && sequences.length > 0 && <ListItem disablePadding>
                        <ListItemButton data-tour="project-sequences-toggle" onClick={() => setSequencesExpanded(!sequencesExpanded)} sx={{ py: 0.75, pr: 1.5 }}>
                            <ListItemIcon sx={{ minWidth: 32, mr: 1 }}>
                                <ViewTimelineOutlinedIcon/>
                            </ListItemIcon>
                            <ListItemText
                                primaryTypographyProps={{ fontSize: 13, fontWeight: 600, noWrap: true }}
                                primary={"Sequences"}
                            />
                            {sequencesExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
                        </ListItemButton>
                    </ListItem>}

                    {sequencesExpanded && (sequences ?? []).map((s: any) => {
                        const primaryNode = (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Box sx={{ px: 1.5, paddingLeft: 0.5, fontWeight: 300, color: 'text.primary', fontSize: 14 }}>{s.title}</Box>
                            </Box>
                        );

                        if (renamingItemId === s.id) {
                            return (
                                <ListItem key={s.id} disablePadding>
                                    <ListItemButton sx={{ py: 0.75, pr: 4, pl: 3 }} onClick={(e) => e.stopPropagation()}>
                                        <ListItemIcon sx={{ minWidth: 32, mr: 1 }}>
                                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'text.secondary' }} />
                                        </ListItemIcon>
                                        <TextField
                                            value={renameValue}
                                            onChange={(e) => setRenameValue(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onBlur={async () => {
                                                const newName = renameValue.trim();
                                                if (newName && newName !== (s.title || s.name || "")) {
                                                    try { await RenameSequence({ sequenceId: s.id, newName }); } catch {}
                                                }
                                                setRenamingItemId(null);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    (async () => {
                                                        const newName = renameValue.trim();
                                                        if (newName && newName !== (s.title || s.name || "")) {
                                                            try { await RenameSequence({ sequenceId: s.id, newName }); } catch {}
                                                        }
                                                        setRenamingItemId(null);
                                                    })();
                                                } else if (e.key === 'Escape') {
                                                    setRenamingItemId(null);
                                                }
                                            }}
                                            autoFocus
                                            size="small"
                                            variant="standard"
                                            sx={{ flex: 1, '& .MuiInput-input': { fontSize: '13px', padding: '0px' } }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        }

                        const handleSequenceMenu = (event: React.MouseEvent) => {
                            event.preventDefault();
                            event.stopPropagation();
                            ContextMenuStore.show(event.clientX, event.clientY, [
                                {
                                    title: "Edit",
                                    icon: () => <ViewTimelineOutlinedIcon />,
                                    onClick: async () => {
                                        handleOpenSequence(s.id);
                                        ContextMenuStore.hide();
                                    },
                                },
                                {
                                    title: "Insert into Script",
                                    icon: () => <LayersOutlinedIcon />,
                                    onClick: async () => {
                                        await handleInsertSequenceIntoScript(s.id);
                                        ContextMenuStore.hide();
                                    },
                                },
                                {
                                    title: "Rename",
                                    icon: () => <ModeEditIcon />,
                                    onClick: () => {
                                        setRenamingItemId(s.id);
                                        setRenameValue(s.title || s.name || "");
                                        ContextMenuStore.hide();
                                    },
                                },
                                {
                                    title: "Delete",
                                    icon: () => <DeleteIcon />,
                                    onClick: () => {
                                        openDialog({
                                            title: `Delete this sequence?`,
                                            message: 'It will be permanently deleted.',
                                            onConfirm: async () => {
                                                await DeleteSequence({ sequenceId: s.id })
                                            },
                                        });
                                        ContextMenuStore.hide();
                                    },
                                },
                            ]);
                        };

                        return (
                            <RowItem
                                key={s.id}
                                icon={<Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'text.secondary' }} />}
                                iconBgColor="transparent"
                                primary={(primaryNode as unknown) as any}
                                dataTour="project-sequence-row"
                                onClick={(e) => {
                                    if (!e) return;
                                    openPreviewForSequence(e as any, s);
                                }}
                                onMenu={handleSequenceMenu}
                            />
                        );
                    })}

                    {/* Folders and their contents */}
                    {folders.map((folder: any) => {
                        const folderId = folder.id;
                        const folderName = folder.name || "Untitled Folder";
                        const isExpanded = expandedFolders.has(folderId);
                        const filesInFolder = getFilesInFolder(folderId);
                        const isDragOver = dragOverFolderId === folderId;

                        // Folder renaming state
                        if (renamingItemId === folderId) {
                            return (
                                <ListItem key={folderId} disablePadding>
                                    <ListItemButton sx={{ py: 0.75, pr: 4 }} onClick={(e) => e.stopPropagation()}>
                                        <ListItemIcon sx={{ minWidth: 32, mr: 1 }}>
                                            <FolderOutlined sx={{ fontSize: 20, color: 'var(--app-text-muted)' }} />
                                        </ListItemIcon>
                                        <TextField
                                            value={renameValue}
                                            onChange={(e) => setRenameValue(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onBlur={async () => {
                                                const newName = renameValue.trim();
                                                if (newName && newName !== folderName) {
                                                    try { await RenameFolder({ folderId, newName }); } catch {}
                                                }
                                                setRenamingItemId(null);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    (async () => {
                                                        const newName = renameValue.trim();
                                                        if (newName && newName !== folderName) {
                                                            try { await RenameFolder({ folderId, newName }); } catch {}
                                                        }
                                                        setRenamingItemId(null);
                                                    })();
                                                } else if (e.key === 'Escape') {
                                                    setRenamingItemId(null);
                                                }
                                            }}
                                            autoFocus
                                            size="small"
                                            variant="standard"
                                            sx={{ flex: 1, '& .MuiInput-input': { fontSize: '13px', padding: '0px' } }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        }

                        return (
                            <React.Fragment key={folderId}>
                                {/* Folder row */}
                                <ListItem
                                    disablePadding
                                    onContextMenu={(e) => handleFolderMenu(e, folder)}
                                    onDragOver={(e) => handleFolderDragOver(e, folderId)}
                                    onDragLeave={handleFolderDragLeave}
                                    onDrop={(e) => handleFolderDrop(e, folderId)}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'var(--app-bg-subtle)',
                                            borderRadius: '6px',
                                        },
                                        '&:hover .row-actions': {
                                            opacity: 1,
                                            pointerEvents: 'auto',
                                        },
                                        ...(isDragOver ? {
                                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                            borderRadius: '6px',
                                            outline: '2px dashed #3B82F6',
                                        } : {}),
                                    }}
                                    secondaryAction={
                                        <IconButton
                                            size="small"
                                            className="row-actions"
                                            onClick={(e) => handleFolderMenu(e, folder)}
                                            sx={{ opacity: 0, transition: 'opacity 0.2s ease' }}
                                        >
                                            <MoreVertIcon fontSize="small" />
                                        </IconButton>
                                    }
                                >
                                    <ListItemButton onClick={() => toggleFolder(folderId)} sx={{ py: 0.75, pr: 4 }}>
                                        <ListItemIcon sx={{ minWidth: 24, mr: 0.5 }}>
                                            {isExpanded ? <KeyboardArrowDown sx={{ fontSize: 18 }} /> : <KeyboardArrowRight sx={{ fontSize: 18 }} />}
                                        </ListItemIcon>
                                        <ListItemIcon sx={{ minWidth: 32, mr: 1 }}>
                                            <FolderOutlined sx={{ fontSize: 20, color: 'var(--app-text-muted)' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primaryTypographyProps={{ fontSize: 13, fontWeight: 500, noWrap: true }}
                                            primary={folderName}
                                        />
                                    </ListItemButton>
                                </ListItem>

                                {/* Files inside folder (when expanded) */}
                                {isExpanded && filesInFolder.map((f) => {
                                    const thumbUrl = thumbs[f.id];
                                    const isTrack = f.type === 'audio' || f.type === 'video';
                                    const durationSec = isTrack ? (f.record?.duration ?? f.record?.Duration ?? undefined) : undefined;
                                    const durationStr = durationSec ? formatDurationBrief(durationSec) : undefined;
                                    const fileReady = isFileReady(f);
                                    const statusLabel = getFileStatusLabel(f);
                                    const transcriptionInfo = getTranscriptionStatus(f);
                                    const showSyncingIndicator = fileReady && !isFileRemoteSynced(f);
                                    const backupAvailableInfo = getBackupAvailableInfo(f);
                                    const isTrackSelected = selectedTrackIds.has(String(f.id));

                                    if (renamingItemId === f.id) {
                                        return (
                                            <ListItem key={f.id} disablePadding>
                                                <ListItemButton sx={{ py: 0.75, pr: 4, pl: 5 }} onClick={(e) => e.stopPropagation()}>
                                                    <ListItemIcon sx={{ minWidth: 32, mr: 1 }}>
                                                        <Box sx={{ width: 28, height: 28, borderRadius: '4px', background: 'var(--app-bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                            {thumbUrl ? (
                                                                <img src={thumbUrl} alt="thumb" style={{ width: 28, height: 28, objectFit: 'cover' }} />
                                                            ) : fileIconFor(f.type)}
                                                        </Box>
                                                    </ListItemIcon>
                                                    <TextField
                                                        value={renameValue}
                                                        onChange={(e) => setRenameValue(e.target.value)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onBlur={() => {
                                                            (async () => {
                                                                const newName = renameValue.trim();
                                                                if (newName && newName !== f.name) {
                                                                    try { await RenameTrackOrAsset({ newName, entityId: f.id, isTrack: f.type !== 'image' }); } catch {}
                                                                }
                                                                setRenamingItemId(null);
                                                            })();
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                (async () => {
                                                                    const newName = renameValue.trim();
                                                                    if (newName && newName !== f.name) {
                                                                        try { await RenameTrackOrAsset({ newName, entityId: f.id, isTrack: f.type !== 'image' }); } catch {}
                                                                    }
                                                                    setRenamingItemId(null);
                                                                })();
                                                            } else if (e.key === 'Escape') {
                                                                setRenamingItemId(null);
                                                                setRenameValue("");
                                                            }
                                                        }}
                                                        autoFocus
                                                        size="small"
                                                        variant="standard"
                                                        sx={{ flex: 1, '& .MuiInput-input': { fontSize: '13px', padding: '0px' } }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        );
                                    }

                                    const primaryNode = (
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontSize: '13px', 
                                                    fontWeight: 400, 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'nowrap', 
                                                    flex: 1,
                                                    color: fileReady ? 'text.primary' : 'text.disabled',
                                                }}
                                            >
                                                {f.name}
                                            </Typography>
                                            {!fileReady && statusLabel && !backupAvailableInfo && (
                                                <Typography 
                                                    variant="caption" 
                                                    sx={{ 
                                                        fontSize: '10px', 
                                                        color: '#F2880C', 
                                                        flexShrink: 0,
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {statusLabel}
                                                </Typography>
                                            )}
                                            {backupAvailableInfo && (
                                                <Tooltip title="Recording incomplete - click to use backup" arrow placement="top">
                                                    <Box 
                                                        component="span"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            e.preventDefault();
                                                            openBackupDialog(f.name, backupAvailableInfo.backupTrackId);
                                                        }}
                                                        sx={{ 
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexShrink: 0,
                                                            cursor: 'pointer',
                                                            pointerEvents: 'auto',
                                                            width: 18,
                                                            height: 18,
                                                            borderRadius: '50%',
                                                            backgroundColor: '#FEF3C7',
                                                            border: '1px solid #FDE68A',
                                                            '&:hover': {
                                                                backgroundColor: '#FDE68A',
                                                            }
                                                        }}
                                                    >
                                                        <InfoOutlinedIcon sx={{ fontSize: 12, color: '#F59E0B' }} />
                                                    </Box>
                                                </Tooltip>
                                            )}
                                            {fileReady && durationStr && (
                                                <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary', flexShrink: 0 }}>
                                                    {durationStr}
                                                </Typography>
                                            )}
                                        </Box>
                                    );

                                    return (
                                        <ListItem
                                            key={f.id}
                                            disablePadding
                                            draggable={fileReady}
                                            onDragStart={(e) => fileReady && handleFileDragStart(e, f.id)}
                                            onDragEnd={handleFileDragEnd}
                                            onContextMenu={(e) => handleContextMenuClick(e as any, {...f, title: f.name, status: fileReady ? "ready" : "processing"}, fileReady)}
	                                    sx={{
                                                backgroundColor: isTrackSelected ? 'var(--app-bg-subtle)' : 'transparent',
                                                borderRadius: '6px',
	                                                '&:hover': { backgroundColor: fileReady ? 'var(--app-bg-subtle)' : 'transparent', borderRadius: '6px' },
	                                                '&:hover .row-actions': { opacity: 1, pointerEvents: 'auto' },
	                                                opacity: draggedFileId === f.id ? 0.5 : 1,
	                                            cursor: fileReady ? 'grab' : 'default',
	                                            }}
                                            secondaryAction={
                                            <IconButton size="small" className="row-actions" onClick={(e) => handleContextMenuClick(e, {...f, title: f.name, status: fileReady ? "ready" : "processing"}, fileReady)} sx={{ opacity: 0, transition: 'opacity 0.2s ease' }}>
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            }
                                        >
	                                <ListItemButton 
                                        data-tour={isTrack ? "project-file-row" : undefined}
	                                    onClick={(e) => { void handleFileRowClick(e as any, f, { allowPendingPlaceholderReplace: true }); }} 
	                                    disabled={!fileReady}
	                                    sx={{ 
	                                        py: 0.75, 
	                                        pr: 4,
                                                    pl: 5,
                                                    borderRadius: '6px',
                                                    backgroundColor: isTrackSelected ? 'var(--app-bg-subtle)' : 'transparent',
                                                    '&.Mui-disabled': {
                                                        opacity: 1, // Keep content visible but show as non-interactive
                                                    }
                                                }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 32, mr: 1 }}>
                                                    <Box sx={{ 
                                                        width: 28, 
                                                        height: 28, 
                                                        borderRadius: '4px', 
                                                        background: 'var(--app-bg-subtle)', 
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center', 
                                                        overflow: 'hidden',
                                                        position: 'relative',
                                                    }}>
                                                        {thumbUrl ? <img src={thumbUrl} alt="thumb" style={{ width: 28, height: 28, objectFit: 'cover', opacity: fileReady ? 1 : 0.5 }} /> : fileIconFor(f.type)}
                                                        {!fileReady && !backupAvailableInfo && (
                                                            <Box sx={{ 
                                                                position: 'absolute', 
                                                                inset: 0, 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                justifyContent: 'center',
                                                                backgroundColor: 'rgba(255,255,255,0.7)',
                                                            }}>
                                                                <CircularProgress size={14} thickness={5} sx={{ color: '#F2880C' }} />
                                                            </Box>
                                                        )}
                                                        {showSyncingIndicator && (
                                                            <Tooltip title="Uploading to server" arrow placement="top">
                                                                <Box sx={{
                                                                    position: 'absolute',
                                                                    top: -2,
                                                                    right: -2,
                                                                    width: 14,
                                                                    height: 14,
                                                                    borderRadius: '3px',
                                                                    backgroundColor: '#EFF6FF',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    border: '1px solid #BFDBFE',
                                                                }}>
                                                                    <CloudUploadOutlinedIcon sx={{ fontSize: 9, color: '#1D4ED8' }} />
                                                                </Box>
                                                            </Tooltip>
                                                        )}
                                                        {transcriptionInfo && (
                                                            <Tooltip title={transcriptionInfo.label} arrow placement="top">
                                                                <Box sx={{ 
                                                                    position: 'absolute', 
                                                                    bottom: -2, 
                                                                    right: -2, 
                                                                    width: 14, 
                                                                    height: 14, 
                                                                    borderRadius: '3px',
                                                                    backgroundColor: transcriptionInfo.isError ? '#FEE2E2' : transcriptionInfo.isSilent ? '#F3F4F6' : '#E0F2FE',
                                                                    display: 'flex', 
                                                                    alignItems: 'center', 
                                                                    justifyContent: 'center',
                                                                    border: '1px solid',
                                                                    borderColor: transcriptionInfo.isError ? '#FECACA' : transcriptionInfo.isSilent ? '#D1D5DB' : '#BAE6FD',
                                                                }}>
                                                                    {transcriptionInfo.isSilent ? (
                                                                        <Box component="span" sx={{ fontSize: 9, color: '#6B7280', fontWeight: 600, lineHeight: 1 }}>∅</Box>
                                                                    ) : (
                                                                        <FormatQuoteIcon sx={{ 
                                                                            fontSize: 9, 
                                                                            color: transcriptionInfo.isError ? '#DC2626' : '#0284C7',
                                                                        }} />
                                                                    )}
                                                                </Box>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </ListItemIcon>
                                                <ListItemText primaryTypographyProps={{ fontSize: 13, fontWeight: 400, noWrap: true }} primary={primaryNode} />
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                            </React.Fragment>
                        );
                    })}

                    {/* Unfiled files (not in any folder) */}
                    {getUnfiledFiles.map((f) => {
                        const thumbUrl = thumbs[f.id];
                        const isTrack = f.type === 'audio' || f.type === 'video';
                        const durationSec = isTrack ? (f.record?.duration ?? f.record?.Duration ?? undefined) : undefined;
                        const durationStr = durationSec ? formatDurationBrief(durationSec) : undefined;
                        const fileReady = isFileReady(f);
                        const statusLabel = getFileStatusLabel(f);
                        const transcriptionInfo = getTranscriptionStatus(f);
                        const showSyncingIndicator = fileReady && !isFileRemoteSynced(f);
                        const backupAvailableInfo = getBackupAvailableInfo(f);
                        const isTrackSelected = selectedTrackIds.has(String(f.id));

                        if (renamingItemId === f.id) {
                            return (
                                <ListItem key={f.id} disablePadding>
                                    <ListItemButton sx={{ py: 0.75, pr: 4 }} onClick={(e) => e.stopPropagation()}>
                                        <ListItemIcon sx={{ minWidth: 32, mr: 1 }}>
                                            <Box sx={{ width: 28, height: 28, borderRadius: '4px', background: 'var(--app-bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                {thumbUrl ? (
                                                    <img src={thumbUrl} alt="thumb" style={{ width: 28, height: 28, objectFit: 'cover' }} />
                                                ) : fileIconFor(f.type)}
                                            </Box>
                                        </ListItemIcon>
                                        <TextField
                                            value={renameValue}
                                            onChange={(e) => setRenameValue(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            onBlur={() => {
                                                (async () => {
                                                    const newName = renameValue.trim();
                                                    if (newName && newName !== f.name) {
                                                        try {
                                                            await RenameTrackOrAsset({
                                                                newName,
                                                                entityId: f.id,
                                                                isTrack: f.type !== 'image',
                                                            });
                                                        } catch (_) {
                                                            // ignore rename failure for now
                                                        }
                                                    }
                                                    setRenamingItemId(null);
                                                })();
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    (async () => {
                                                        const newName = renameValue.trim();
                                                        if (newName && newName !== f.name) {
                                                            try {
                                                                await RenameTrackOrAsset({
                                                                    newName,
                                                                    entityId: f.id,
                                                                    isTrack: f.type !== 'image',
                                                                });
                                                            } catch (_) {
                                                                // ignore
                                                            }
                                                        }
                                                        setRenamingItemId(null);
                                                    })();
                                                } else if (e.key === 'Escape') {
                                                    setRenamingItemId(null);
                                                    setRenameValue("");
                                                }
                                            }}
                                            autoFocus
                                            size="small"
                                            variant="standard"
                                            sx={{ flex: 1, '& .MuiInput-input': { fontSize: '13px', padding: '0px' } }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            );
                        }

                        const primaryNode = (
                            <Box display="flex" alignItems="center" gap={0.5}>
                                <Typography
                                    variant="body2"
                                    sx={{ 
                                        fontSize: '13px', 
                                        fontWeight: 400, 
                                        overflow: 'hidden', 
                                        textOverflow: 'ellipsis', 
                                        whiteSpace: 'nowrap', 
                                        flex: 1,
                                        color: fileReady ? 'text.primary' : 'text.disabled',
                                    }}
                                >
                                    {f.name}
                                </Typography>
                                {!fileReady && statusLabel && !backupAvailableInfo && (
                                    <Typography 
                                        variant="caption" 
                                        sx={{ 
                                            fontSize: '10px', 
                                            color: '#F2880C', 
                                            flexShrink: 0,
                                            fontWeight: 500,
                                        }}
                                    >
                                        {statusLabel}
                                    </Typography>
                                )}
                                {backupAvailableInfo && (
                                    <Tooltip title="Recording incomplete - click to use backup" arrow placement="top">
                                        <Box 
                                            component="span"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                openBackupDialog(f.name, backupAvailableInfo.backupTrackId);
                                            }}
                                            sx={{ 
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                                cursor: 'pointer',
                                                pointerEvents: 'auto',
                                                width: 18,
                                                height: 18,
                                                borderRadius: '50%',
                                                backgroundColor: '#FEF3C7',
                                                border: '1px solid #FDE68A',
                                                '&:hover': {
                                                    backgroundColor: '#FDE68A',
                                                }
                                            }}
                                        >
                                            <InfoOutlinedIcon sx={{ fontSize: 12, color: '#F59E0B' }} />
                                        </Box>
                                    </Tooltip>
                                )}
                                {fileReady && durationStr && (
                                    <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary', flexShrink: 0 }}>
                                        {durationStr}
                                    </Typography>
                                )}
                            </Box>
                        );

                        return (
                            <ListItem
                                key={f.id}
                                disablePadding
                                draggable={fileReady}
                                onDragStart={(e) => fileReady && handleFileDragStart(e, f.id)}
                                onDragEnd={handleFileDragEnd}
                                onContextMenu={(e) => handleContextMenuClick(e as any, {...f, title: f.name, status: fileReady ? "ready" : "processing"}, fileReady)}
                                sx={{
                                    backgroundColor: isTrackSelected ? 'var(--app-bg-subtle)' : 'transparent',
                                    borderRadius: '6px',
                                    '&:hover': { backgroundColor: fileReady ? 'var(--app-bg-subtle)' : 'transparent', borderRadius: '6px' },
                                    '&:hover .row-actions': { opacity: 1, pointerEvents: 'auto' },
                                    opacity: draggedFileId === f.id ? 0.5 : 1,
                                    cursor: fileReady ? 'grab' : 'default',
                                }}
                                secondaryAction={
                                    <IconButton
                                        size="small"
                                        className="row-actions"
                                        onClick={(e) => handleContextMenuClick(e, {...f, title: f.name, status: fileReady ? "ready" : "processing"}, fileReady)}
                                        sx={{ opacity: 0, transition: 'opacity 0.2s ease' }}
                                    >
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                }
                            >
                                <ListItemButton 
                                    data-tour={isTrack ? "project-file-row" : undefined}
                                    onClick={(e) => { void handleFileRowClick(e as any, f); }} 
                                    disabled={!fileReady}
                                    sx={{ 
                                        py: 0.75, 
                                        pr: 4,
                                        borderRadius: '6px',
                                        backgroundColor: isTrackSelected ? 'var(--app-bg-subtle)' : 'transparent',
                                        '&.Mui-disabled': {
                                            opacity: 1, // Keep content visible but show as non-interactive
                                        }
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 32, mr: 1 }}>
                                        <Box sx={{ 
                                            width: 28, 
                                            height: 28, 
                                            borderRadius: '4px', 
                                            background: 'var(--app-bg-subtle)', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            overflow: 'hidden',
                                            position: 'relative',
                                        }}>
                                            {thumbUrl ? (
                                                <img src={thumbUrl} alt="thumb" style={{ width: 28, height: 28, objectFit: 'cover', opacity: fileReady ? 1 : 0.5 }} />
                                ) : fileIconFor(f.type)}
                                            {!fileReady && !backupAvailableInfo && (
                                                <Box sx={{ 
                                                    position: 'absolute', 
                                                    inset: 0, 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center',
                                                    backgroundColor: 'rgba(255,255,255,0.7)',
                                                }}>
                                                    <CircularProgress size={14} thickness={5} sx={{ color: '#F2880C' }} />
                                                </Box>
                                            )}
                                            {showSyncingIndicator && (
                                                <Tooltip title="Uploading to server" arrow placement="top">
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        top: -2,
                                                        right: -2,
                                                        width: 14,
                                                        height: 14,
                                                        borderRadius: '3px',
                                                        backgroundColor: '#EFF6FF',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        border: '1px solid #BFDBFE',
                                                    }}>
                                                        <CloudUploadOutlinedIcon sx={{ fontSize: 9, color: '#1D4ED8' }} />
                                                    </Box>
                                                </Tooltip>
                                            )}
                                            {transcriptionInfo && (
                                                <Tooltip title={transcriptionInfo.label} arrow placement="top">
                                                    <Box sx={{ 
                                                        position: 'absolute', 
                                                        bottom: -2, 
                                                        right: -2, 
                                                        width: 14, 
                                                        height: 14, 
                                                        borderRadius: '3px',
                                                        backgroundColor: transcriptionInfo.isError ? '#FEE2E2' : transcriptionInfo.isSilent ? '#F3F4F6' : '#E0F2FE',
                                                        display: 'flex', 
                                                        alignItems: 'center', 
                                                        justifyContent: 'center',
                                                        border: '1px solid',
                                                        borderColor: transcriptionInfo.isError ? '#FECACA' : transcriptionInfo.isSilent ? '#D1D5DB' : '#BAE6FD',
                                                    }}>
                                                        {transcriptionInfo.isSilent ? (
                                                            <Box component="span" sx={{ fontSize: 9, color: '#6B7280', fontWeight: 600, lineHeight: 1 }}>∅</Box>
                                                        ) : (
                                                            <FormatQuoteIcon sx={{ 
                                                                fontSize: 9, 
                                                                color: transcriptionInfo.isError ? '#DC2626' : '#0284C7',
                                                            }} />
                                                        )}
                                                    </Box>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </ListItemIcon>
                                    <ListItemText
                                        primaryTypographyProps={{ fontSize: 13, fontWeight: 400, noWrap: true }}
                                        primary={primaryNode}
                                    />
                                </ListItemButton>
                            </ListItem>
                        );
                    })}
                </List>
            </PaperPanel>

            {/* Add menu (Files panel + button) */}
            <Menu
                anchorEl={addMenuAnchor}
                open={Boolean(addMenuAnchor)}
                onClose={() => setAddMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { p: 1, borderRadius: 2, minWidth: 260 } }}
            >
                <Box sx={{ px: 1.5, paddingBottom: 0.5, fontWeight: 500, color: 'text.primary', fontSize: 14 }}>Add from</Box>
                <MenuItem onClick={() => {
                    setAddMenuAnchor(null);
                    // Use file picker to select files, then validate and upload
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = 'video/*,audio/*,image/*';
                    input.multiple = true;
                    input.onchange = async () => {
                        const files = Array.from(input.files ?? []);
                        if (files.length === 0) return;
                        
                        const { localOkFiles, rejectedFiles } = await validateFilesForUpload(files, {
                            allowServerProcessing: false,
                        });
                        const shouldShowReview = rejectedFiles.length > 0;
                        if (shouldShowReview) {
                            setUnsupportedFiles(rejectedFiles);
                        }

                        if (localOkFiles.length === 0) {
                            if (shouldShowReview) setUnsupportedFilesDialogOpen(true);
                            input.remove();
                            return;
                        }

                        const projectId = ProjectAssetsStore.projectId as string;
                        if (!projectId) {
                            input.remove();
                            return;
                        }

	                        const imageFiles = localOkFiles.filter((f) => isImageFile(f));
	                        const mediaFiles = localOkFiles.filter((f) => !isImageFile(f));

	                        const willOpenCombine = mediaFiles.length > 1;
	                        if (shouldShowReview) {
	                            if (willOpenCombine) setOpenReviewAfterCombine(true);
	                            else setUnsupportedFilesDialogOpen(true);
	                        }

                        // Upload images immediately (no sequence grouping).
                        for (const file of imageFiles) {
                            await UploadAsset({
                                projectId,
                                projectType: LibrettoProjectTypes.ProjectTypeEditor,
                                file,
                                filetype: getUploadFiletype(file),
                                languageCode: "auto",
                            });
                        }

	                        if (!willOpenCombine) {
	                            for (const file of mediaFiles) {
	                                await UploadAsset({
	                                    projectId,
	                                    projectType: LibrettoProjectTypes.ProjectTypeEditor,
	                                    file,
                                    filetype: getUploadFiletype(file),
                                    languageCode: "auto",
                                });
                            }
                            input.remove();
                            return;
                        }

                        // Multi-file audio/video upload: ask if it should be combined into a Sequence.
                        setPendingCombineFiles(mediaFiles.map((file) => ({ file })));
                        setCombineDialogOpen(true);
                        setMultiUploadAssets([]);
                        setMultiUploadMode(null);
                        setSequenceIdForMultiUpload(null);
                        setSequenceTitleForMultiUpload(null);
                        multiUploadProcessingRef.current = false;

                        const uploadPromises = mediaFiles.map(async (file, uploadIndex) => {
                            try {
                                const assetId = await UploadAsset({
                                    projectId,
                                    projectType: LibrettoProjectTypes.ProjectTypeEditor,
                                    file,
                                    filetype: getUploadFiletype(file),
                                    languageCode: "auto",
                                });
                                if (!assetId) return null;
                                return { assetId, fileName: file.name, uploadIndex };
                            } catch (e) {
                                console.warn("[V2ProjectSidebar] Upload failed for multi-upload", file?.name, e);
                                return null;
                            }
                        });

                        const results = await Promise.all(uploadPromises);
                        const validUploads = results.filter(
                            (r): r is { assetId: string; fileName: string; uploadIndex: number } => r !== null,
                        );
                        setMultiUploadAssets(validUploads);
                        
                        input.remove();
                    };
                    input.click();
                }} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><ComputerOutlinedIcon fontSize="small"/></ListItemIcon>
                    <ListItemText primary="Computer" primaryTypographyProps={{ fontSize: 14 }} />
                </MenuItem>

                <Divider sx={{ my: 1 }} />
                <Box sx={{ px: 1.5, paddingBottom: 0.5, fontWeight: 500, color: 'text.primary', fontSize: 14 }}>Create</Box>
                <MenuItem onClick={async () => { setAddMenuAnchor(null); await handleCreateNewSequence(); }} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><ViewTimelineOutlinedIcon fontSize="small"/></ListItemIcon>
                    <ListItemText primary="Sequence" primaryTypographyProps={{ fontSize: 14 }} />
                </MenuItem>
                <MenuItem onClick={async () => { 
                    setAddMenuAnchor(null); 
                    const folderId = await CreateFolder({ projectId: ProjectAssetsStore.projectId as string });
                    // Auto-expand and start renaming the new folder
                    if (folderId) {
                        setExpandedFolders(prev => new Set(prev).add(folderId));
                        setRenamingItemId(folderId);
                        setRenameValue("Untitled Folder");
                    }
                }} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}><CreateNewFolderOutlined fontSize="small"/></ListItemIcon>
                    <ListItemText primary="Folder" primaryTypographyProps={{ fontSize: 14 }} />
                </MenuItem>
            </Menu>

            <AssetPreviewPopover
                anchorEl={previewAnchorEl}
                item={previewItem}
                onClose={handleClosePreview}
                onInsert={(it) => handleInsertIntoScriptLayer(it)}
                onAddNewLayer={(it) => handleAddToComposition(it)}
                multiSelectCount={previewMode === "multi" ? selectedTrackIds.size : 0}
                multiSelectItems={previewMode === "multi" ? selectedPreviewItems : []}
                onCreateSequence={() => {
                    void createSequenceFromSelectedTracks().catch((error) => {
                        console.error("[V2ProjectSidebar] Failed to create sequence from selected tracks", error);
                    });
                }}
                hideInsertIntoScript={Boolean(
                    (ApplicationStore.peekPendingAddLayerContext?.() ?? null) ||
                        (ApplicationStore.peekPendingAddLayerFromSelectionContext?.() ?? null),
                )}
            />

            <SequenceActionsPopover
                anchorEl={sequencePopoverAnchorEl}
                sequence={sequencePopoverItem}
                onClose={handleCloseSequencePopover}
                onInsertIntoScript={async (sequenceId) => {
                    try {
                        await handleInsertSequenceIntoScript(sequenceId);
                    } catch (e) {
                        console.error("[V2ProjectSidebar] Failed to insert sequence into script", e);
                    }
                }}
                onEditSequence={(sequenceId) => handleOpenSequence(sequenceId)}
            />

            <CombineFilesDialog
                isOpen={combineDialogOpen}
                onClose={handleKeepSeparateMultiUpload}
                files={pendingCombineFiles}
                onKeepSeparate={handleKeepSeparateMultiUpload}
                onCombineIntoSequence={handleCombineIntoSequenceMultiUpload}
            />

            {/* Unsupported Files Dialog */}
            <UnsupportedFilesDialog
                isOpen={unsupportedFilesDialogOpen}
                onClose={() => {
                    setUnsupportedFilesDialogOpen(false);
                    setUnsupportedFiles([]);
                }}
                files={unsupportedFiles}
            />

            <DownloadInfoCard
                state={downloadFeedback}
                onDismiss={dismissDownloadCard}
                onCancel={cancelDownload}
            />

        </Box>
    );
});
